import * as d3 from 'd3';
import clsx from 'clsx';

import {getTheme} from '@semantical/theme';
import {VisualVocabulary} from '@semantical/components/visual-vocabulary';
import {ConceptSchemeViewer} from '@semantical/components/concept-scheme-viewer';

import { SchemaClass, SchemaIndividual } from "@semantical/modules/ontology-model";
import { Individual, Relationship, KnowledgeGraph } from "@semantical/modules/knowledge-graph";
import type { Node, Element, Workspace as IWorkspace, VocabularyRef } from "@semantical/modules/semantical-core";
import { isNode } from "@semantical/modules/semantical-core";
import {DiagramEditor, DiagramClassElement} from "@semantical/components/diagram-editor";
import {Concept, Vocabulary} from "@semantical/modules/vocabulary";
import Properties from "@semantical/components/properties";
import React from "react";
import Diagram from "@semantical/modules/diagram/Diagram.ts";
import DiagramClass from "@semantical/modules/diagram/DiagramClass.ts";
import DiagramRelationship from "@semantical/modules/diagram/DiagramRelationship.ts";
import {Chat, Advice, ConceptAdvice, NewConceptAction} from "@semantical/components/chat";
import {Toolbox} from "@semantical/components/toolbox";
import Browser from "@semantical/components/browser/Browser";
import { WordCloud } from "@semantical/components/word-cloud";
import {
    ClassTaxonomyNode, ClassHierarchyMap,
    loadDataProperties, ClassDataPropertiesMap,
    loadObjectPropertyRestrictions, ClassObjectPropertyRestrictionsMap,
    RelationshipTaxonomyNode,
    ObjectProperty, RestrictedObjectProperty, PrefixMap
} from "@semantical/modules/ontology-model";
import {
    DomainConfig, getDefaultDomain, getDomainById, getDomainByNamespace,
    OntologyEnabledBoundedContext,
    createOntologySchemaFromDomainAsync, createOntologySchemaFromOntology,
    registerUrlDomain, createUrlDomainConfig, isUrlLoadedDomain, getUrlDomainBySourceUrl,
    type UrlDomainConfig,
    type WorkspaceConfigRoot,
    getAllBoundedContextConfigs,
    findContextIdForView,
    findContextIdForVocabulary,
    findViewConfigById,
    addViewToConfig,
    addDatasetToConfig
} from "@semantical/modules/workspace";
import type { Domain } from "@semantical/modules/semantical-core";
import { remoteOntologyLoader } from "@semantical/services/ontology";
import { parseTurtle } from "@semantical/modules/owl2/turtle/parser.ts";
import { isClassAssertion, isAnnotationAssertion, isObjectPropertyAssertion, isDataPropertyAssertion } from "@semantical/modules/owl2/types/Axiom.ts";
import { iriToString } from "@semantical/modules/owl2/types/IRI.ts";
import { TitleBar } from "@semantical/components/title-bar";
import { StatusBar, ToggleButton } from "@semantical/components/status-bar";
import { PersistenceService, LocalStorageAdapter } from "@semantical/services/persistence";
import { InferenceEngine, CompositionServesRule, InversePropertyRule, PropertyChainRule } from "@semantical/modules/inference";
import BoundedContextInfoModal from "@semantical/components/bounded-context-info-modal/BoundedContextInfoModal";
import { LoadUrlModal } from "@semantical/components/load-url-modal";
import { LoadingModal } from "@semantical/components/loading-modal";
import { createExportService, type ExportService } from "@semantical/services/export";
import { extractLocalName, slugifyForUriSegment, inferBaseUri } from "@semantical/utils/iri";
import { findClassNodeByUri, findRelationshipNodeByUri, getClassLabelFromTaxonomy } from "@semantical/utils/taxonomy";

type Props = {
    vocabulary: Vocabulary;
    workspace: IWorkspace;
    workspaceConfig: WorkspaceConfigRoot;
    diagramId: string | undefined;
    vocabularyId?: string;
    navigateToDiagram?: (diagramId: string) => void;
    navigateToVocabulary?: (vocabularyUri: string) => void;
}

type State = {
    selectedElement: Element | null;
    viewMode: 'diagram' | 'visual-vocabulary' | 'concept-scheme';  // Toggle between diagram, visual vocabulary, and concept scheme views
    proposedConcepts: Concept[];
    showTurtleModal: boolean;
    turtleText: string;
    turtleModalTitle: string;
    showBoundedContextInfoModal: boolean;
    showLoadUrlModal: boolean;
    newConceptName: string;
    addingInline: boolean;
    draftTopConceptName: string;
    draggingConceptUri: string | null;
    classTaxonomy: ClassTaxonomyNode[] | null;
    relationshipTaxonomy: RelationshipTaxonomyNode[] | null;
    toolboxSelectedClass: ClassTaxonomyNode | null;
    dataPropertiesMap: ClassDataPropertiesMap | null;
    objectPropertyRestrictionsMap: ClassObjectPropertyRestrictionsMap | null;
    classHierarchy: ClassHierarchyMap | null;
    domainPrefixes: PrefixMap;
    activeDomain: DomainConfig;
    showChat: boolean;
    hoveredToolboxClass: ClassTaxonomyNode | null;
    hoveredBrowserElement: Element | null;
    darkMode: boolean;
    // Combined TTL content (main domain + dependencies) for DiagramEditor
    combinedTtl: string;
    // Allowed namespaces for filtering (main + imports + dependencies)
    allowedNamespaces: string[];
    // Map of predicate URIs to highlight colors for WordCloud feature
    highlightedPredicates: Map<string, string>;
    // Increment to force re-render when namespace colors change
    namespaceColorsVersion: number;
    // Loading modal state
    showLoadingModal: boolean;
    loadingStatus: string;
    // Version counter to trigger re-render when diagram/KnowledgeGraph changes in workspace
    diagramVersion: number;
    // Browser panel width (persisted)
    browserWidth: number;
    // Selected vocabulary URI for ConceptSchemeViewer
    selectedVocabularyUri: string | null;
}

class App extends React.Component<Props, State> {

    diagramContainer: React.RefObject<HTMLElement | null>;
    visualVocabularyContainer: React.RefObject<HTMLElement | null>;

    diagramEditor: React.RefObject<DiagramEditor | null>;
    visualVocabulary: React.RefObject<VisualVocabulary | null>;
    persistenceService: PersistenceService;
    inferenceEngine: InferenceEngine;
    exportService: ExportService;

    // Lifecycle management for StrictMode compatibility
    private _isMounted: boolean = false;
    private _keydownHandler: ((event: KeyboardEvent) => void) | null = null;
    private _loadingModalTimeout: ReturnType<typeof setTimeout> | null = null;
    private _loadingStartTime: number = 0;
    // Flag to prevent saving until initial restoration is complete
    private _restorationComplete: boolean = false;

    state: State;

    /**
     * Get the bounded context for the current diagram.
     * Returns undefined if no diagram is open or bounded context not loaded.
     */
    get boundedContext(): OntologyEnabledBoundedContext | undefined {
        const diagram = this.diagram;
        if (!diagram) return undefined;
        // Find the bounded context for this diagram (checks registry first, then hierarchy)
        const bc = this.props.workspace.findBoundedContext(diagram.boundedContextId);
        // Cast to OntologyEnabledBoundedContext since we know loaded contexts have ontology support
        return bc as OntologyEnabledBoundedContext | undefined;
    }

    /**
     * Get the selected vocabulary for ConceptSchemeViewer.
     * Returns the vocabulary from the bounded context's UbiquitousLanguage if available,
     * otherwise falls back to the main vocabulary passed via props.
     */
    get selectedVocabulary(): Vocabulary {
        if (this.state.selectedVocabularyUri) {
            // First try the current bounded context (if we have one from a diagram)
            if (this.boundedContext) {
                const vocabRef = this.boundedContext.getUbiquitousLanguage()?.getVocabularyByUri(this.state.selectedVocabularyUri);
                if (vocabRef?.vocabulary) {
                    return vocabRef.vocabulary;
                }
            }
            // If no diagram-based context, find the context by vocabulary URI
            const contextId = findContextIdForVocabulary(this.props.workspaceConfig, this.state.selectedVocabularyUri);
            if (contextId) {
                const bc = this.props.workspace.findBoundedContext(contextId) as OntologyEnabledBoundedContext | undefined;
                if (bc) {
                    const vocabRef = bc.getUbiquitousLanguage()?.getVocabularyByUri(this.state.selectedVocabularyUri);
                    if (vocabRef?.vocabulary) {
                        return vocabRef.vocabulary;
                    }
                }
            }
        }
        return this.props.vocabulary;
    }

    /**
     * Get the active diagram from its owning bounded context (based on URL diagramId prop).
     * Returns undefined if no diagram ID in URL or diagram not yet loaded.
     */
    get diagram(): Diagram | undefined {
        if (!this.props.diagramId) return undefined;
        // First, check registered bounded contexts
        for (const bc of this.props.workspace.getBoundedContexts()) {
            const ontologyBc = bc as OntologyEnabledBoundedContext;
            const view = ontologyBc.getView(this.props.diagramId);
            if (view) return view as Diagram;
        }
        // Fall back to searching all BCs in the domain hierarchy
        for (const domain of this.props.workspace.getRootDomains()) {
            const diagram = this.findDiagramInDomain(domain, this.props.diagramId);
            if (diagram) return diagram;
        }
        return undefined;
    }

    /**
     * Recursively search for a diagram in a domain hierarchy.
     */
    private findDiagramInDomain(domain: Domain, diagramId: string): Diagram | undefined {
        // Check bounded contexts in this domain
        for (const bc of domain.getBoundedContexts()) {
            const ontologyBc = bc as OntologyEnabledBoundedContext;
            const view = ontologyBc.getView(diagramId);
            if (view) return view as Diagram;
        }
        // Search child domains
        for (const child of domain.getChildren()) {
            const diagram = this.findDiagramInDomain(child, diagramId);
            if (diagram) return diagram;
        }
        return undefined;
    }

    /**
     * Get the KnowledgeGraph for the active diagram.
     * Returns undefined if no diagram ID in URL or diagram not yet loaded.
     * Uses diagram.datasetId when available, otherwise falls back to diagram ID.
     */
    get knowledgeGraph(): KnowledgeGraph | undefined {
        if (!this.props.diagramId) return undefined;
        const bc = this.boundedContext;
        if (!bc) return undefined;
        // Use diagram's linked datasetId if available, otherwise fall back to diagram ID
        const datasetId = this.diagram?.datasetId ?? this.props.diagramId;
        return bc.getKnowledgeGraphByDatasetId(datasetId);
    }

    /**
     * Get or load a bounded context by ID.
     * If the bounded context is already registered, returns it immediately.
     * Otherwise, finds the BoundedContext in the hierarchy and configures it.
     */
    getOrLoadBoundedContext(contextId: string): OntologyEnabledBoundedContext | undefined {
        // Check if already registered in workspace
        if (this.props.workspace.hasBoundedContext(contextId)) {
            return this.props.workspace.findBoundedContext(contextId) as OntologyEnabledBoundedContext;
        }

        // Find the BoundedContext in the domain hierarchy
        const boundedContext = this.props.workspace.findBoundedContext(contextId);
        if (!boundedContext) {
            console.error(`Bounded context not found: ${contextId}`);
            return undefined;
        }

        // The BoundedContext is actually an OntologyEnabledBoundedContext instance
        const bc = boundedContext as OntologyEnabledBoundedContext;

        // Configure the bounded context with ontology loader
        this.configureBoundedContextLoader(bc);

        // Register in workspace
        this.props.workspace.registerBoundedContext(bc);
        console.debug(`Loaded bounded context: ${contextId} (${bc.name})`);

        return bc;
    }

    /**
     * Configure a bounded context with the ontology loader for remote loading support.
     */
    private configureBoundedContextLoader(bc: OntologyEnabledBoundedContext): void {
        bc.setOntologyLoader(async (iri: string) => {
            // 1. First check local domain registry with IRI normalization
            const normalizations = [
                iri,
                iri.endsWith('#') ? iri.slice(0, -1) : iri + '#',
                iri.endsWith('/') ? iri.slice(0, -1) : iri + '/'
            ];
            let domain: DomainConfig | undefined;
            for (const ns of normalizations) {
                domain = getDomainByNamespace(ns);
                if (domain) break;
            }
            if (domain) {
                console.debug(`App.ontologyLoader: "${iri}" found in local domain registry (matched: ${domain.ns})`);
                return createOntologySchemaFromDomainAsync(domain);
            }

            // 2. Try remote loading
            console.debug(`App.ontologyLoader: Attempting remote load for: ${iri}`);
            try {
                const result = await remoteOntologyLoader.load(iri);
                if (result.success && result.ontology) {
                    console.debug(`App.ontologyLoader: Successfully loaded "${iri}"`);
                    const schema = createOntologySchemaFromOntology(result.ontology, iri);
                    return schema;
                }
                if (result.error) {
                    console.warn(`App.ontologyLoader: Failed to load "${iri}": ${result.error}`);
                }
            } catch (error) {
                console.error(`App.ontologyLoader: Exception loading "${iri}":`, error);
            }
            return undefined;
        });
    }

    /**
     * Trigger a re-render after modifying diagram or KnowledgeGraph in bc.
     * Call this after any mutation to workspace diagram data.
     */
    notifyDiagramChanged = (callback?: () => void) => {
        this.setState(
            prevState => ({ diagramVersion: prevState.diagramVersion + 1 }),
            callback
        );
    }

    constructor(props: Props) {
        super(props);

        // Initialize refs
        this.diagramContainer = React.createRef<HTMLElement>();
        this.visualVocabularyContainer = React.createRef<HTMLElement>();
        this.diagramEditor = React.createRef<DiagramEditor>();
        this.visualVocabulary = React.createRef<VisualVocabulary>();

        // Initialize persistence service
        this.persistenceService = new PersistenceService(new LocalStorageAdapter());

        // Initialize inference engine with rules
        this.inferenceEngine = new InferenceEngine();
        this.inferenceEngine.registerRule(new CompositionServesRule());
        this.inferenceEngine.registerRule(new InversePropertyRule());
        this.inferenceEngine.registerRule(new PropertyChainRule());

        // Initialize export service
        this.exportService = createExportService();

        // Workspace is now passed as prop - verify we have bounded contexts
        const boundedContextConfigs = getAllBoundedContextConfigs(props.workspaceConfig);
        console.debug('App: Using workspace from props', {
            domainCount: props.workspace.getRootDomains().length,
            boundedContextCount: boundedContextConfigs.length
        });

        if (boundedContextConfigs.length === 0) {
            throw new Error('No bounded contexts found in workspace config');
        }

        // If a diagram ID is provided, pre-load its bounded context
        if (props.diagramId) {
            const diagramOwnerContextId = findContextIdForView(props.workspaceConfig, props.diagramId);
            if (diagramOwnerContextId) {
                console.debug(`App: Pre-loading bounded context for diagram ${props.diagramId} (context: ${diagramOwnerContextId})`);
                this.getOrLoadBoundedContext(diagramOwnerContextId);
            }
        }

        // If a vocabulary ID is provided, pre-load its bounded context
        if (props.vocabularyId) {
            const vocabularyOwnerContextId = findContextIdForVocabulary(props.workspaceConfig, props.vocabularyId);
            if (vocabularyOwnerContextId) {
                console.debug(`App: Pre-loading bounded context for vocabulary ${props.vocabularyId} (context: ${vocabularyOwnerContextId})`);
                this.getOrLoadBoundedContext(vocabularyOwnerContextId);
            }
        }

        // Initialize default state
        // If vocabularyId is provided, start in concept-scheme view
        this.state = {
            selectedElement: null,
            viewMode: props.vocabularyId ? 'concept-scheme' : 'diagram',
            proposedConcepts: [],
            showTurtleModal: false,
            turtleText: "",
            turtleModalTitle: "Vocabulary (Turtle)",
            showBoundedContextInfoModal: false,
            showLoadUrlModal: false,
            newConceptName: "",
            addingInline: false,
            draftTopConceptName: "",
            draggingConceptUri: null,
            classTaxonomy: null,
            relationshipTaxonomy: null,
            toolboxSelectedClass: null,
            dataPropertiesMap: null,
            objectPropertyRestrictionsMap: null,
            classHierarchy: null,
            domainPrefixes: new Map(),
            activeDomain: getDefaultDomain(),
            showChat: false,
            hoveredToolboxClass: null,
            hoveredBrowserElement: null,
            darkMode: true,
            combinedTtl: getDefaultDomain().ttl,
            allowedNamespaces: [getDefaultDomain().ns],
            highlightedPredicates: new Map(),
            namespaceColorsVersion: 0,
            showLoadingModal: false,
            loadingStatus: '',
            diagramVersion: 0,
            browserWidth: 240,
            selectedVocabularyUri: props.vocabularyId ?? null
        };

        // Check if we have stored data for this diagram and get domain from it
        if (props.diagramId) {
            const storedDomainId = this.persistenceService.getDiagramDomainId(props.diagramId);
            if (storedDomainId) {
                const savedDomain = getDomainById(storedDomainId);
                if (savedDomain) {
                    this.state.activeDomain = savedDomain;
                    console.debug('App: Restored active domain from diagram data', savedDomain.id);
                }
            }
        }

        // Restore app config (showChat, darkMode, browserWidth)
        // Note: editingMode, inferenceEnabled, showStereotype, showOptionalProperties, showEssentialProperties are now per-diagram
        const configResult = this.persistenceService.loadAppConfig();
        if (configResult.success && configResult.data) {
            this.state.showChat = configResult.data.showChat;
            this.state.darkMode = configResult.data.darkMode ?? true;
            this.state.browserWidth = configResult.data.browserWidth ?? 240;
            console.debug('App: Restored app config from localStorage', configResult.data);
        }

        // Note: Domain model and diagram are restored in componentDidMount after ontology data is loaded
        // This ensures the modelClassResolver can properly resolve dataProperties and objectPropertyRestrictions
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, _snapshot?: any) {
        // Handle diagram ID change (e.g., from React Router navigation)
        if (prevProps.diagramId !== this.props.diagramId) {
            console.debug('App.componentDidUpdate: diagramId changed', {
                from: prevProps.diagramId,
                to: this.props.diagramId
            });
            // Reset restoration flag and reload data for new diagram
            this._restorationComplete = false;
            this.loadDomainData(this.state.activeDomain, true);
            return;
        }

        const diagramId = this.props.diagramId;
        const diagram = this.diagram;
        const knowledgeGraph = this.knowledgeGraph;

        // Don't save until initial restoration is complete (prevents overwriting persisted data)
        if (!this._restorationComplete) {
            console.debug('App.componentDidUpdate: Skip save - restoration not complete');
            return;
        }

        // Don't save until diagram exists in workspace (or no diagram is active)
        if (!diagramId || !diagram || !knowledgeGraph) {
            console.debug('App.componentDidUpdate: Skip save - missing data', { diagramId, hasDiagram: !!diagram, hasKG: !!knowledgeGraph });
            return;
        }

        // Persist knowledgeGraph and diagram to localStorage
        // Use dataset ID if diagram links to a dataset, otherwise use diagram ID for legacy KGs
        const kgStorageKey = diagram.datasetId ?? diagramId;
        console.debug('App.componentDidUpdate: Saving', {
            diagramId,
            datasetId: diagram.datasetId,
            kgStorageKey,
            domainId: this.state.activeDomain.id,
            modelingLanguageIri: diagram.modelingLanguageIri,
            classCount: diagram.classes.length,
            kgIndividuals: knowledgeGraph.individuals.size
        });
        this.persistenceService.saveKnowledgeGraph(kgStorageKey, knowledgeGraph, this.state.activeDomain.id);
        this.persistenceService.saveDiagram(diagram, this.state.activeDomain.id);

        // Persist app config if it changed
        // Note: editingMode, inferenceEnabled, showStereotype, showOptionalProperties, showEssentialProperties are saved with diagram
        if (this.state.showChat !== prevState.showChat ||
            this.state.darkMode !== prevState.darkMode ||
            this.state.browserWidth !== prevState.browserWidth) {
            this.persistenceService.saveAppConfig({
                showChat: this.state.showChat,
                darkMode: this.state.darkMode,
                browserWidth: this.state.browserWidth
            });
        }
    }

    componentDidMount() {
        console.debug("App mounted");

        // Guard against StrictMode double-mount
        if (this._isMounted) {
            console.debug("App: Skipping duplicate mount (StrictMode)");
            return;
        }
        this._isMounted = true;

        // Apply the initial theme
        this.applyTheme(this.state.darkMode);

        // Only append SVG if not already present (StrictMode safety)
        const container = d3.select(this.diagramContainer.current);
        if (container.select("svg").empty()) {
            container.append("svg")
                .attr("width", "100%")
                .attr("height", "100%");
        }

        // Store handler reference for cleanup
        this._keydownHandler = (event: KeyboardEvent) => {
            if (event.key === 'Delete') {
                this.diagramEditor.current?.deleteSelected();
            }
            if (event.key === 'Escape') {
                this.cancelConceptPlacement();
            }
        };
        document.addEventListener('keydown', this._keydownHandler);

        // Register persisted URL domains (no fetch - just from stored metadata)
        this.reloadPersistedUrlDomains();

        // Load domain data and restore persisted data
        this.loadDomainData(this.state.activeDomain, true);

        // If viewing a vocabulary, load it after a short delay to allow bounded context to initialize
        if (this.props.vocabularyId) {
            setTimeout(() => {
                this.loadVocabularyIfNeeded(this.props.vocabularyId!);
            }, 100);
        }
    }

    /**
     * Reload URL domains that were persisted from previous sessions.
     * Registers them in the runtime registry so they appear in the dropdown.
     * Does NOT fetch the ontologies - just registers them from stored metadata.
     */
    reloadPersistedUrlDomains(): void {
        const persistedUrlDomains = this.persistenceService.loadUrlDomains();

        if (persistedUrlDomains.length === 0) {
            console.debug("App: No persisted URL domains to reload");
            return;
        }

        console.debug(`App: Registering ${persistedUrlDomains.length} persisted URL domains`);

        for (const { url, domainId, label, ns } of persistedUrlDomains) {
            // Register directly from persisted metadata - no fetch needed!
            const urlDomainConfig: UrlDomainConfig = {
                id: domainId,
                label,
                ttl: '',
                ns,
                sourceUrl: url,
                isUrlLoaded: true,
            };
            registerUrlDomain(urlDomainConfig);
            console.debug(`App: Registered persisted URL domain: ${label} (${domainId})`);
        }
    }

    componentWillUnmount() {
        console.debug("App unmounting");
        this._isMounted = false;

        // Clean up loading modal timeout
        if (this._loadingModalTimeout) {
            clearTimeout(this._loadingModalTimeout);
            this._loadingModalTimeout = null;
        }

        // Clean up event listener
        if (this._keydownHandler) {
            document.removeEventListener('keydown', this._keydownHandler);
            this._keydownHandler = null;
        }

        // Clean up SVG element
        const container = d3.select(this.diagramContainer.current);
        container.select("svg").remove();
    }

    /**
     * Close the loading modal, with a finishing message if loading took a while.
     */
    closeLoadingModal = () => {
        // Clear any pending timeout
        if (this._loadingModalTimeout) {
            clearTimeout(this._loadingModalTimeout);
        }

        const elapsedTime = Date.now() - this._loadingStartTime;

        // Only show finishing message if loading took more than 3 seconds
        if (elapsedTime > 3000) {
            this.setState({ loadingStatus: 'Connecting the dots...' });

            // Close after a delay to let the message be visible
            this._loadingModalTimeout = setTimeout(() => {
                if (this._isMounted) {
                    this.setState({ showLoadingModal: false, loadingStatus: '' });
                }
                this._loadingModalTimeout = null;
            }, 800);
        } else {
            // Quick load - close immediately
            this.setState({ showLoadingModal: false, loadingStatus: '' });
        }
    }

    async loadDomainData(domain: DomainConfig, restorePersisted: boolean = false) {
        // Guard: need a bounded context (from current diagram) to load domain data into
        const bc = this.boundedContext;
        if (!bc) {
            console.debug('loadDomainData: No bounded context available (no diagram open)');
            return;
        }

        // Reset restoration flag if we're going to restore persisted data
        // This prevents componentDidUpdate from saving during loading
        if (restorePersisted) {
            this._restorationComplete = false;
        }

        // Check if this is a URL-loaded domain
        if (isUrlLoadedDomain(domain.id)) {
            await this.loadUrlDomainData(domain as UrlDomainConfig, restorePersisted);
            return;
        }

        // Show loading modal and track start time
        this._loadingStartTime = Date.now();
        this.setState({ showLoadingModal: true, loadingStatus: `Loading ${domain.label}...` });

        // Aggregate TTL content from main domain and all dependencies
        const ttlParts: string[] = [domain.ttl];

        // Clear workspace and load the domain's ontology (and its imports)
        bc.clear();
        try {
            // Load the primary domain ontology
            const schema = await createOntologySchemaFromDomainAsync(domain);

            // Check if component unmounted during async operation
            if (!this._isMounted) {
                console.debug("App: Aborting loadDomainData - component unmounted");
                this.setState({ showLoadingModal: false, loadingStatus: '' });
                return;
            }

            this.setState({ loadingStatus: `Processing ${domain.label}...` });
            await bc.addOntology(schema);

            // Load domain dependencies (domains used via prefix but not declared in owl:imports)
            if (domain.dependencies) {
                console.debug('Loading dependencies:', domain.dependencies);
                const depCount = domain.dependencies.length;
                let depIndex = 0;
                for (const depNs of domain.dependencies) {
                    if (!this._isMounted) {
                        this.setState({ showLoadingModal: false, loadingStatus: '' });
                        return;
                    }

                    depIndex++;
                    const depDomain = getDomainByNamespace(depNs);
                    console.debug(`Dependency ${depNs}: found domain =`, depDomain?.id, 'hasOntology =', bc.hasOntology(depNs));
                    if (depDomain && !bc.hasOntology(depNs)) {
                        this.setState({ loadingStatus: `Loading dependency ${depIndex}/${depCount}: ${depDomain.label}...` });
                        try {
                            const depSchema = await createOntologySchemaFromDomainAsync(depDomain);
                            if (!this._isMounted) {
                                this.setState({ showLoadingModal: false, loadingStatus: '' });
                                return;
                            }
                            await bc.addOntology(depSchema);
                            // Add dependency TTL to the aggregated content for taxonomy builders
                            ttlParts.push(depDomain.ttl);
                            console.debug(`Added dependency TTL for ${depNs}, ttlParts length:`, ttlParts.length);
                        } catch (depErr) {
                            console.warn(`Failed to load dependency ontology ${depNs}:`, depErr);
                        }
                    }
                }
            }

            console.debug("Workspace loaded ontologies:", bc.size, "prefixes:", bc.getPrefixes());
        } catch (err) {
            console.error("Failed to load ontology into workspace:", err);
            this.setState({ showLoadingModal: false, loadingStatus: '' });
            return;
        }

        // Load namespace colors into workspace
        const savedColors = this.persistenceService.loadNamespaceColors();
        for (const [namespace, color] of savedColors) {
            bc.setNamespaceColor(namespace, color);
        }

        // Check mounted state before proceeding
        if (!this._isMounted) {
            this.setState({ showLoadingModal: false, loadingStatus: '' });
            return;
        }

        this.setState({ loadingStatus: `Building taxonomies...` });

        // Get aggregated prefixes from all loaded ontologies in workspace
        const domainPrefixes = bc.getPrefixes();

        // Build taxonomy data from all loaded ontologies in workspace (aggregates automatically!)
        const classTaxonomy = bc.getClassTaxonomy(domain.ns);
        const relationshipTaxonomy = bc.getRelationshipTaxonomy(domain.ns);
        const classHierarchy = bc.getClassHierarchy();

        // Get all namespaces for TTL processing
        const allowedNamespaces = bc.getNamespaces();
        console.debug("Loaded namespaces from workspace:", allowedNamespaces);
        console.debug("Loaded ontologies:", bc.getAllOntologies().map(o => ({
            name: o.name,
            ontologyIRI: o.ontologyIRI,
            namespace: o.getNamespace(),
            classCount: o.classes.size
        })));
        console.debug("Class taxonomy size:", classTaxonomy.length);

        console.debug("Loaded ontology data from workspace:", {
            classTaxonomy,
            relationshipTaxonomy,
            classHierarchy,
            domainPrefixes
        });
        console.debug("Relationship taxonomy size:", relationshipTaxonomy.length, "dcterms props:", relationshipTaxonomy.filter(r => r.uri?.includes('purl.org/dc/terms')).length);

        // Note: dataPropertiesMap and objectPropertyRestrictionsMap still need TTL for now
        // These can be migrated to workspace methods in a future refactor
        const combinedTtl = ttlParts.join('\n\n');

        Promise.all([
            loadDataProperties(combinedTtl, domain.ns, allowedNamespaces),
            loadObjectPropertyRestrictions(combinedTtl, domain.ns, allowedNamespaces)
        ]).then(([dataPropertiesMap, objectPropertyRestrictionsMap]) => {
            // Guard against unmounted state
            if (!this._isMounted) {
                console.debug("App: Aborting ontology data processing - component unmounted");
                this.setState({ showLoadingModal: false, loadingStatus: '' });
                return;
            }

            console.debug("Loaded additional ontology data:", { dataPropertiesMap, objectPropertyRestrictionsMap });

            // Update state with ontology data
            this.setState({
                classTaxonomy,
                dataPropertiesMap,
                objectPropertyRestrictionsMap,
                relationshipTaxonomy,
                classHierarchy,
                domainPrefixes,
                combinedTtl,
                allowedNamespaces
            });

            // Close loading modal with finishing message
            this.closeLoadingModal();

            // Set the class hierarchy on the knowledge graph for subclass checking
            this.knowledgeGraph?.setClassHierarchy(classHierarchy);

            // Set the ontology schema on the knowledge graph for inference rules
            // Try to find by namespace first, then fall back to any ontology with the matching namespace
            let primaryOntology = bc.getOntologyByIRI(domain.ns);
            if (!primaryOntology) {
                // The workspace stores by ontologyIRI (e.g. http://example.org/party#PartyDomainOntology)
                // but domain.ns is just the namespace (e.g. http://example.org/party#)
                // Find an ontology whose namespace matches
                primaryOntology = bc.getAllOntologies().find(
                    schema => schema.getNamespace() === domain.ns
                );
            }
            if (primaryOntology) {
                this.knowledgeGraph?.setOntologySchema(primaryOntology);
                console.debug('App: Set ontology schema on knowledge graph:', primaryOntology.ontologyIRI);
            } else {
                console.warn('App: Could not find ontology for namespace:', domain.ns);
            }

            // If we need to restore persisted data, do it now with the resolvers
            if (restorePersisted) {
                this.restorePersistedData(classTaxonomy, dataPropertiesMap, objectPropertyRestrictionsMap, relationshipTaxonomy, classHierarchy);
            }
        }).catch(err => {
            console.error("Failed to load ontology data:", err);
            this.setState({ showLoadingModal: false, loadingStatus: '' });
        });
    }

    /**
     * Load data for a URL-loaded domain.
     * Similar to loadDomainData but loads from URL instead of embedded TTL string.
     */
    async loadUrlDomainData(domain: UrlDomainConfig, restorePersisted: boolean = false) {
        // Guard: need a workspace (from current diagram) to load domain data into
        const bc = this.boundedContext;
        if (!bc) {
            console.debug('loadUrlDomainData: No workspace available (no diagram open)');
            return;
        }

        console.debug(`App: Loading URL domain: ${domain.label} from ${domain.sourceUrl}`);

        // Show loading modal and track start time
        this._loadingStartTime = Date.now();
        this.setState({ showLoadingModal: true, loadingStatus: `Loading ${domain.label}...` });

        // Progress callback to update loading status
        const onProgress = (status: string) => {
            if (this._isMounted) {
                this.setState({ loadingStatus: status });
            }
        };

        // Clear workspace and reload from URL
        bc.clear();

        try {
            const result = await bc.loadFromUrl(domain.sourceUrl, onProgress);

            if (!this._isMounted) {
                console.debug("App: Aborting loadUrlDomainData - component unmounted");
                this.setState({ showLoadingModal: false, loadingStatus: '' });
                return;
            }

            if (!result.success) {
                console.error("Failed to reload URL domain:", result.error);
                this.setState({ showLoadingModal: false, loadingStatus: '' });
                return;
            }

            console.debug("Workspace loaded URL domain:", bc.size, "ontologies");
        } catch (err) {
            console.error("Failed to load URL domain into workspace:", err);
            this.setState({ showLoadingModal: false, loadingStatus: '' });
            return;
        }

        // Load namespace colors into workspace
        const savedColors = this.persistenceService.loadNamespaceColors();
        for (const [namespace, color] of savedColors) {
            bc.setNamespaceColor(namespace, color);
        }

        if (!this._isMounted) return;

        // Get taxonomy data from workspace
        const domainPrefixes = bc.getPrefixes();
        const classTaxonomy = bc.getClassTaxonomy(domain.ns);
        const relationshipTaxonomy = bc.getRelationshipTaxonomy(domain.ns);
        const classHierarchy = bc.getClassHierarchy();
        const allowedNamespaces = bc.getNamespaces();

        console.debug("Loaded URL domain data:", {
            classTaxonomy: classTaxonomy.length,
            relationshipTaxonomy: relationshipTaxonomy.length,
            ontologies: bc.size
        });

        // URL domains don't have embedded TTL, use empty string
        // Data/object properties will be loaded from workspace ontologies
        const combinedTtl = '';

        Promise.all([
            loadDataProperties(combinedTtl, domain.ns, allowedNamespaces),
            loadObjectPropertyRestrictions(combinedTtl, domain.ns, allowedNamespaces)
        ]).then(([dataPropertiesMap, objectPropertyRestrictionsMap]) => {
            if (!this._isMounted) {
                console.debug("App: Aborting URL domain data processing - component unmounted");
                this.setState({ showLoadingModal: false, loadingStatus: '' });
                return;
            }

            this.setState({
                classTaxonomy,
                dataPropertiesMap,
                objectPropertyRestrictionsMap,
                relationshipTaxonomy,
                classHierarchy,
                domainPrefixes,
                combinedTtl,
                allowedNamespaces
            });

            // Close loading modal with finishing message
            this.closeLoadingModal();

            // Set class hierarchy on knowledge graph
            this.knowledgeGraph?.setClassHierarchy(classHierarchy);

            // Set ontology schema on knowledge graph
            const primaryOntology = bc.getAllOntologies().find(
                schema => schema.getNamespace() === domain.ns
            );
            if (primaryOntology) {
                this.knowledgeGraph?.setOntologySchema(primaryOntology);
                console.debug('App: Set URL domain ontology schema on knowledge graph:', primaryOntology.ontologyIRI);
            }

            if (restorePersisted) {
                this.restorePersistedData(classTaxonomy, dataPropertiesMap, objectPropertyRestrictionsMap, relationshipTaxonomy, classHierarchy);
            }
        }).catch(err => {
            console.error("Failed to load URL domain ontology data:", err);
            this.setState({ showLoadingModal: false, loadingStatus: '' });
        });
    }

    /**
     * Restore domain model and diagram from persistence, using the loaded ontology data
     * to properly resolve ModelClass with dataProperties and objectPropertyRestrictions,
     * and ObjectProperty with domain/range constraints.
     */
    restorePersistedData(
        classTaxonomy: ClassTaxonomyNode[],
        dataPropertiesMap: ClassDataPropertiesMap,
        objectPropertyRestrictionsMap: ClassObjectPropertyRestrictionsMap,
        relationshipTaxonomy: RelationshipTaxonomyNode[],
        classHierarchy: ClassHierarchyMap
    ) {
        // Guard: need a bounded context to restore diagram data into
        const boundedContext = this.boundedContext;
        if (!boundedContext) {
            console.debug('restorePersistedData: No bounded context available');
            this._restorationComplete = true;
            return;
        }

        // Create a modelClassResolver that uses the loaded ontology data
        // IMPORTANT: Always return a valid SchemaClass to avoid fallback using individual's label
        const modelClassResolver = (uri: string): SchemaClass | undefined => {
            const dataProperties = dataPropertiesMap.get(uri);
            const subClassOf = objectPropertyRestrictionsMap.get(uri);

            // Try to find the class in taxonomy to get its label
            const classNode = findClassNodeByUri(classTaxonomy, uri);

            // Extract label: prefer taxonomy label, otherwise extract from URI
            const label = classNode?.label ||
                (uri.includes('#') ? uri.split('#').pop()! : uri.split('/').pop()!);

            // Create a placeholder concept for the TBox class
            const placeholderConcept = new Concept(uri, label, [], [], "");

            // Always return a valid SchemaClass (even if no properties)
            // This prevents Individual.fromJSON from using incorrect fallback labels
            return new SchemaClass('class', placeholderConcept, {
                label,
                stereotype: classNode?.stereotype,
                categoryColor: classNode?.categoryColor,
                dataProperties,
                subClassOf
            });
        };

        // Create an objectPropertyResolver that uses the loaded relationship taxonomy
        // Always returns a valid ObjectProperty to ensure labels display correctly
        const objectPropertyResolver = (uri: string): ObjectProperty | undefined => {
            const node = findRelationshipNodeByUri(relationshipTaxonomy, uri);
            if (!node) {
                console.debug(`objectPropertyResolver: Property not found in taxonomy: ${uri}`);
                console.debug(`Available properties:`, relationshipTaxonomy.flatMap(n => [n.uri, ...(n.children?.map(c => c.uri) || [])]));
                // Return a minimal property with label extracted from URI
                return {
                    uri,
                    label: extractLocalName(uri)
                };
            }

            return {
                uri: node.uri || uri,
                label: node.label,
                relationshipType: node.type,
                strokeStyle: node.strokeStyle,
                sourceMarker: node.sourceMarker,
                targetMarker: node.targetMarker,
                // Domain/range constraints as OWL2 Class Expressions
                domain: node.domain,
                range: node.range
            };
        };

        // TBox individual resolver: delegate to bounded context
        const tboxIndividualResolver = (uri: string) => boundedContext.getIndividualByUri(uri);

        // Skip diagram restoration if no diagram ID (no active diagram)
        const diagramId = this.props.diagramId;
        if (!diagramId) {
            console.debug('App.restorePersistedData: No diagram ID, skipping diagram restoration');
            this._restorationComplete = true;
            return;
        }

        // Get datasetId from persisted diagram (single source of truth for datasetId)
        // If diagram links to a dataset, use datasetId for KG loading; otherwise use diagramId (legacy)
        const storedDatasetId = this.persistenceService.getDiagramDatasetId(diagramId);
        const kgStorageKey = storedDatasetId ?? diagramId;
        console.debug('App.restorePersistedData: KG storage key:', { diagramId, storedDatasetId, kgStorageKey });

        // Get view config for name (workspace config stores view metadata - id, name, viewType)
        const viewConfig = findViewConfigById(this.props.workspaceConfig, diagramId);

        // Load knowledge graph with all resolvers
        const knowledgeGraphResult = this.persistenceService.loadKnowledgeGraph(
            kgStorageKey,
            this.props.vocabulary,
            modelClassResolver,
            objectPropertyResolver,
            tboxIndividualResolver
        );

        console.debug(`App.restorePersistedData: KG load result - success=${knowledgeGraphResult.success}, hasData=${!!knowledgeGraphResult.data}, discarded=${knowledgeGraphResult.discarded}`);
        if (knowledgeGraphResult.success && knowledgeGraphResult.data) {
            const restoredKnowledgeGraph = knowledgeGraphResult.data;
            console.debug(`App.restorePersistedData: Restored KG has ${restoredKnowledgeGraph.individuals.size} individuals`);

            // Set the class hierarchy on the restored knowledge graph for subclass checking
            restoredKnowledgeGraph.setClassHierarchy(classHierarchy);

            // Set the ontology schema on the restored knowledge graph for inference rules
            const domain = this.state.activeDomain;
            let primaryOntology = boundedContext.getOntologyByIRI(domain.ns);
            if (!primaryOntology) {
                primaryOntology = boundedContext.getAllOntologies().find(
                    schema => schema.getNamespace() === domain.ns
                );
            }
            if (primaryOntology) {
                restoredKnowledgeGraph.setOntologySchema(primaryOntology);
                console.debug('App: Set ontology schema on restored knowledge graph:', primaryOntology.ontologyIRI);
            }

            // Load diagram with the restored knowledge graph using diagram ID
            // Pass bounded context to resolve SchemaIndividuals from TBox ontologies
            const diagramResult = this.persistenceService.loadDiagram(diagramId, restoredKnowledgeGraph, boundedContext);
            console.debug('App.restorePersistedData: Diagram load result', {
                success: diagramResult.success,
                hasData: !!diagramResult.data,
                discarded: diagramResult.discarded,
                error: diagramResult.error
            });
            if (diagramResult.success && diagramResult.data) {
                const diagram = diagramResult.data;
                console.debug('App.restorePersistedData: Loaded diagram', {
                    id: diagram.id,
                    modelingLanguageIri: diagram.modelingLanguageIri,
                    classCount: diagram.classes.length
                });

                // Resolve any placeholder SchemaIndividuals that couldn't be found during fromJSON
                const resolved = diagram.resolvePlaceholders(boundedContext);
                if (resolved > 0) {
                    console.debug(`App: Resolved ${resolved} placeholder SchemaIndividual(s) from bounded context`);
                }

                // Store in bounded context
                diagram.datasetId = restoredKnowledgeGraph.id ?? diagramId;
                boundedContext.registerView(diagram);
                boundedContext.setDataset(
                    { id: diagram.datasetId, name: restoredKnowledgeGraph.name || 'UserKnowledgeGraph', type: 'KnowledgeGraph' },
                    restoredKnowledgeGraph
                );
                this.notifyDiagramChanged(() => {
                    // Run inference after restoration if enabled
                    if (this.diagram?.inferenceEnabled) {
                        this.runInference();
                    }
                });
                console.debug('App: Restored knowledgeGraph and diagram from localStorage');
            } else {
                // KG loaded but no diagram - create new diagram
                const diagramName = viewConfig?.name ?? 'New Diagram';
                const diagram = new Diagram(diagramName, boundedContext.id, diagramId);
                diagram.datasetId = restoredKnowledgeGraph.id ?? diagramId;
                boundedContext.registerView(diagram);
                boundedContext.setDataset(
                    { id: diagram.datasetId, name: restoredKnowledgeGraph.name || 'UserKnowledgeGraph', type: 'KnowledgeGraph' },
                    restoredKnowledgeGraph
                );
                this.notifyDiagramChanged(() => {
                    if (this.diagram?.inferenceEnabled) {
                        this.runInference();
                    }
                });
                console.debug('App: Restored knowledgeGraph from localStorage (created new diagram)');
            }
        } else {
            // No persisted data - create fresh diagram without KG
            // User can select a KG later via DatasetSelector
            const diagramName = viewConfig?.name ?? 'New Diagram';
            const diagram = new Diagram(diagramName, boundedContext.id, diagramId);
            diagram.datasetId = null;
            boundedContext.registerView(diagram);
            this.notifyDiagramChanged();
            console.debug('App: No persisted data found, created new diagram (no KG)');
        }

        // Add the view reference to workspace config (so it persists across sessions)
        addViewToConfig(this.props.workspaceConfig, boundedContext.id, diagramId, this.diagram?.name ?? 'New Diagram', 'Diagram');

        // Sync Toolbox with diagram's modelingLanguageIri (may override domain-loaded taxonomy)
        this.syncToolboxWithModelingLanguage();

        // Mark restoration as complete - now componentDidUpdate can save safely
        this._restorationComplete = true;
        console.debug('App.restorePersistedData: Restoration complete');
    }

    /**
     * Sync the Toolbox and DiagramEditor with the diagram's modelingLanguageIri.
     * If the diagram has a specific modeling language set, rebuild taxonomy and property maps for that ontology.
     * If null, clear the taxonomy (Toolbox hidden).
     */
    syncToolboxWithModelingLanguage = async () => {
        const bc = this.boundedContext;
        if (!bc) {
            // No workspace - clear taxonomies
            this.setState({
                classTaxonomy: null,
                relationshipTaxonomy: null,
                dataPropertiesMap: null,
                objectPropertyRestrictionsMap: null
            });
            return;
        }

        const modelingLanguageIri = this.diagram?.modelingLanguageIri;

        if (modelingLanguageIri === null || modelingLanguageIri === undefined) {
            // No modeling language selected - clear the Toolbox taxonomy
            this.setState({
                classTaxonomy: null,
                relationshipTaxonomy: null,
                dataPropertiesMap: null,
                objectPropertyRestrictionsMap: null
            });
        } else {
            // Try to find the ontology by IRI (with various normalizations)
            const ontologySchema = this.findOntologyByIri(modelingLanguageIri);
            if (ontologySchema) {
                const ontologyNs = ontologySchema.getNamespace();
                // Build taxonomy for just this ontology's namespace
                const classTaxonomy = bc.getClassTaxonomy(ontologyNs, [ontologyNs]);
                const relationshipTaxonomy = bc.getRelationshipTaxonomy(ontologyNs, [ontologyNs]);
                const classHierarchy = bc.getClassHierarchy();

                // Find the domain to get TTL for data/object properties
                const domain = this.findDomainByNamespace(ontologyNs);
                console.debug(`syncToolbox: ontologyNs=${ontologyNs}, domain=${domain?.label}, hasTtl=${!!domain?.ttl}, ttlLength=${domain?.ttl?.length}`);
                if (domain?.ttl) {
                    // Build property maps from the domain's TTL
                    const [dataPropertiesMap, objectPropertyRestrictionsMap] = await Promise.all([
                        loadDataProperties(domain.ttl, ontologyNs, [ontologyNs]),
                        loadObjectPropertyRestrictions(domain.ttl, ontologyNs, [ontologyNs])
                    ]);

                    console.debug(`Synced Toolbox with modelingLanguage: ${modelingLanguageIri}, namespace: ${ontologyNs}, classes: ${classTaxonomy.length}, dataProps: ${dataPropertiesMap.size}, objProps: ${objectPropertyRestrictionsMap.size}`);

                    this.setState({
                        classTaxonomy,
                        relationshipTaxonomy,
                        classHierarchy,
                        dataPropertiesMap,
                        objectPropertyRestrictionsMap
                    });

                    // Update knowledge graph's class hierarchy
                    this.knowledgeGraph?.setClassHierarchy(classHierarchy);
                } else {
                    // No TTL available, just update taxonomies
                    console.debug(`Synced Toolbox with modelingLanguage: ${modelingLanguageIri}, namespace: ${ontologyNs}, classes: ${classTaxonomy.length} (no TTL for properties)`);
                    this.setState({
                        classTaxonomy,
                        relationshipTaxonomy,
                        classHierarchy
                    });
                    this.knowledgeGraph?.setClassHierarchy(classHierarchy);
                }
            } else {
                // Ontology not loaded yet - try to load it
                console.debug(`Ontology not loaded for modelingLanguageIri: ${modelingLanguageIri}, attempting to load...`);
                await this.loadOntologyByIri(modelingLanguageIri);

                // Try again after loading
                const loadedOntology = this.findOntologyByIri(modelingLanguageIri);
                if (loadedOntology) {
                    // Recursively sync now that ontology is loaded
                    await this.syncToolboxWithModelingLanguage();
                } else {
                    console.warn(`Failed to load ontology for modelingLanguageIri: ${modelingLanguageIri}`);
                    console.debug(`Available ontologies:`, bc.getAllOntologies().map(o => o.ontologyIRI));
                    // Clear taxonomy when ontology not found
                    this.setState({
                        classTaxonomy: null,
                        relationshipTaxonomy: null
                    });
                }
            }
        }
    }

    /**
     * Find a domain config by namespace IRI.
     */
    findDomainByNamespace = (ns: string): DomainConfig | undefined => {
        const normalizations = [
            ns,
            ns.endsWith('#') ? ns.slice(0, -1) : ns + '#',
            ns.endsWith('/') ? ns.slice(0, -1) : ns + '/'
        ];

        for (const normalized of normalizations) {
            const domain = getDomainByNamespace(normalized);
            if (domain) return domain;
        }
        return undefined;
    }

    /**
     * Find an ontology schema by IRI, trying various normalizations.
     * Handles cases where the IRI might have or lack trailing # or /.
     */
    findOntologyByIri = (iri: string): import("@semantical/modules/ontology-model").OntologySchema | undefined => {
        const bc = this.boundedContext;
        if (!bc) return undefined;

        // Try exact match first
        let schema = bc.getOntologyByIRI(iri);
        if (schema) return schema;

        // Try without trailing #
        if (iri.endsWith('#')) {
            schema = bc.getOntologyByIRI(iri.slice(0, -1));
            if (schema) return schema;
        } else {
            // Try with trailing #
            schema = bc.getOntologyByIRI(iri + '#');
            if (schema) return schema;
        }

        // Try without trailing /
        if (iri.endsWith('/')) {
            schema = bc.getOntologyByIRI(iri.slice(0, -1));
            if (schema) return schema;
        } else {
            // Try with trailing /
            schema = bc.getOntologyByIRI(iri + '/');
            if (schema) return schema;
        }

        // Search by namespace match
        for (const ontology of bc.getAllOntologies()) {
            const ns = ontology.getNamespace();
            // Normalize both for comparison (remove trailing # or /)
            const normalizedIri = iri.replace(/[#/]$/, '');
            const normalizedNs = ns.replace(/[#/]$/, '');
            if (normalizedIri === normalizedNs) {
                return ontology;
            }
        }

        return undefined;
    }

    /**
     * Get ontologies imported by the current KnowledgeGraph for the dropdown.
     * These are ontologies that have been used to create elements in the KG.
     */
    getKgOntologiesForDropdown = (): { iri: string; label: string }[] => {
        if (!this.knowledgeGraph) return [];

        const result: { iri: string; label: string }[] = [];
        const imports = this.knowledgeGraph.getImports();

        for (const [ontologyIri] of imports) {
            // Try to find the ontology in the bounded context to get a proper label
            const ontology = this.boundedContext?.getOntologyByIRI(ontologyIri);
            const label = ontology?.name ?? this.extractLabelFromIri(ontologyIri);

            // Use the namespace as the IRI for selection (matching how contextOntologies work)
            const namespace = ontology?.getNamespace() ?? ontologyIri;

            result.push({ iri: namespace, label });
        }

        return result;
    }

    /**
     * Extract a human-readable label from an ontology IRI.
     */
    private extractLabelFromIri(iri: string): string {
        // Remove trailing # or /
        const cleanIri = iri.replace(/[#/]$/, '');
        // Get the last segment
        const lastSegment = cleanIri.split(/[#/]/).pop() || iri;
        // Convert camelCase/PascalCase to spaces
        return lastSegment.replace(/([a-z])([A-Z])/g, '$1 $2');
    }

    /**
     * Get domain ontologies for the ModelingLanguageSelector dropdown.
     * Returns ALL ontologies from ALL domains in the configuration, excluding
     * those already listed in the context's SemanticLayer (to avoid duplicates).
     */
    getDomainOntologiesForDropdown = (): { iri: string; label: string; domainLabel?: string }[] => {
        const bc = this.boundedContext;
        if (!bc) return [];

        // Get context ontology IRIs (from BC's SemanticLayer config)
        const contextIris = new Set([
            ...bc.getModel().getImportedOntologies().map(o => o.iri),
            ...bc.getModel().getLocalOntologies().map(o => o.iri)
        ]);

        // Normalize IRI for comparison (remove trailing # or /)
        const normalizeIri = (iri: string) => iri.replace(/[#/]$/, '');

        // Collect all ontologies from all domains (recursive)
        const allDomainOntologies: { iri: string; label: string; domainLabel?: string }[] = [];
        const seenIris = new Set<string>();

        const collectFromDomain = (domain: Domain) => {
            const domainLabel = domain.name;
            // Get ontologies from this domain
            for (const ontology of domain.getOntologies()) {
                const iri = ontology.id; // In DomainOntologyRef, id is the IRI
                const normalizedIri = normalizeIri(iri);

                // Skip if already in context or already seen
                if (seenIris.has(normalizedIri)) continue;

                let isInContext = false;
                for (const contextIri of contextIris) {
                    if (normalizeIri(contextIri) === normalizedIri) {
                        isInContext = true;
                        break;
                    }
                }
                if (isInContext) continue;

                seenIris.add(normalizedIri);
                allDomainOntologies.push({
                    iri,
                    label: ontology.label,
                    domainLabel
                });
            }

            // Recurse into child domains
            for (const child of domain.getChildren()) {
                collectFromDomain(child);
            }
        };

        // Traverse all root domains
        for (const rootDomain of this.props.workspace.getRootDomains()) {
            collectFromDomain(rootDomain);
        }

        return allDomainOntologies;
    }

    handleDomainChange = (domain: DomainConfig) => {
        console.debug("Domain changed to:", domain.label);
        this.setState({
            activeDomain: domain,
            classTaxonomy: null,
            relationshipTaxonomy: null,
            dataPropertiesMap: null,
            objectPropertyRestrictionsMap: null,
            classHierarchy: null,
            toolboxSelectedClass: null
        }, () => {
            // Restore persisted data with new domain's taxonomy so relationships resolve correctly
            this.loadDomainData(domain, true);
        });
    }

    /**
     * Handle modeling language change in the TitleBar.
     * This updates the diagram's modeling language property and rebuilds the Toolbox taxonomy.
     * If the ontology is not loaded, it will be loaded on-demand.
     */
    handleModelingLanguageChange = async (iri: string | null) => {
        if (!this.diagram) return;

        console.debug("Modeling language changed to:", iri);
        this.diagram.setModelingLanguage(iri);

        // If an IRI is selected, ensure the ontology is loaded
        if (iri) {
            const existingOntology = this.findOntologyByIri(iri);
            if (!existingOntology) {
                // Ontology not loaded - try to load it from domain registry
                console.debug(`Ontology not loaded for IRI: ${iri}, attempting to load...`);
                await this.loadOntologyByIri(iri);
            }
        }

        // Sync Toolbox with the new modeling language
        await this.syncToolboxWithModelingLanguage();
    }

    /**
     * Load an ontology by IRI from the domain registry.
     * Tries various IRI normalizations to find the matching domain.
     */
    loadOntologyByIri = async (iri: string): Promise<void> => {
        const bc = this.boundedContext;
        if (!bc) return;

        // Try to find domain by namespace (with various normalizations)
        const normalizations = [
            iri,
            iri.endsWith('#') ? iri.slice(0, -1) : iri + '#',
            iri.endsWith('/') ? iri.slice(0, -1) : iri + '/'
        ];

        let domain: DomainConfig | undefined;
        for (const ns of normalizations) {
            domain = getDomainByNamespace(ns);
            if (domain) break;
        }

        if (!domain) {
            console.warn(`No domain found for IRI: ${iri}`);
            return;
        }

        console.debug(`Loading ontology from domain: ${domain.label} (${domain.id})`);

        try {
            const schema = await createOntologySchemaFromDomainAsync(domain);
            bc.addOntology(schema);
            console.debug(`Loaded ontology: ${schema.ontologyIRI}`);
        } catch (err) {
            console.error(`Failed to load ontology for IRI ${iri}:`, err);
        }
    }

    /**
     * Handle labeling context change in the TitleBar.
     * This updates the diagram's labeling context (SKOS vocabulary) for terminology guidance.
     */
    handleLabelingContextChange = (uri: string | null) => {
        if (this.diagram) {
            console.debug("Labeling context changed to:", uri);
            this.diagram.setLabelingContext(uri);
            this.forceUpdate();
        }
    }

    /**
     * Handle dataset change in the TitleBar.
     * This updates the diagram's linked dataset (KnowledgeGraph) for new element creation.
     */
    handleDatasetChange = (id: string | null) => {
        if (this.diagram) {
            console.debug("Dataset changed to:", id);
            this.diagram.datasetId = id;

            // If a dataset is selected and no KnowledgeGraph instance exists, create one
            // This handles datasets loaded from config that only have a reference
            if (id && this.boundedContext) {
                const existingKG = this.boundedContext.getKnowledgeGraphByDatasetId(id);
                if (!existingKG) {
                    // Find the dataset reference to get its name
                    const datasetRef = this.boundedContext.getDatasets().find(d => d.id === id);
                    if (datasetRef) {
                        console.debug("Creating KnowledgeGraph instance for dataset:", datasetRef.name);
                        // Generate namespace based on domain
                        const domainNs = this.state.activeDomain?.ns ?? 'http://example.org/';
                        const kgNamespace = domainNs.endsWith('#')
                            ? domainNs.slice(0, -1) + '/data/'
                            : domainNs + 'data/';
                        // Create the KnowledgeGraph instance
                        const knowledgeGraph = new KnowledgeGraph(datasetRef.name, kgNamespace, undefined, id);
                        // Register it with the bounded context
                        this.boundedContext.setDataset(datasetRef, knowledgeGraph);
                    }
                }
            }

            // datasetId is persisted via saveDiagram() in componentDidUpdate
            // forceUpdate will cause the knowledgeGraph getter to return the newly linked KG
            this.forceUpdate();
        }
    }

    /**
     * Handle creating a new dataset (KnowledgeGraph) from the Browser.
     * @param name - Name for the new KG
     * @param contextId - The BC where the KG should be created
     */
    handleCreateDataset = (name: string, contextId: string) => {
        // Get or load the target workspace
        const targetWorkspace = this.getOrLoadBoundedContext(contextId);
        if (!targetWorkspace) {
            console.error(`Cannot create dataset: workspace not found for context ${contextId}`);
            return;
        }

        // Generate a namespace for the KG
        const domainNs = this.state.activeDomain?.ns ?? 'http://example.org/';
        const kgNamespace = domainNs.endsWith('#')
            ? domainNs.slice(0, -1) + '/data/'
            : domainNs + 'data/';

        // Create new KnowledgeGraph with unique ID
        const kgId = crypto.randomUUID();
        const knowledgeGraph = new KnowledgeGraph(name, kgNamespace, undefined, kgId);

        // Register as a dataset in the target workspace
        const datasetRef = {
            id: kgId,
            name: name,
            type: 'KnowledgeGraph'
        };
        targetWorkspace.setDataset(datasetRef, knowledgeGraph);

        // Add to workspace config for persistence
        addDatasetToConfig(this.props.workspaceConfig, contextId, kgId, name, 'KnowledgeGraph');

        // Save the KG data to localStorage (even though it's empty, establishes the entry)
        const domainId = this.state.activeDomain?.id ?? 'default';
        this.persistenceService.saveKnowledgeGraph(kgId, knowledgeGraph, domainId);

        console.debug(`Created new KnowledgeGraph: ${name} (${kgId}) in context ${contextId}`);
        this.forceUpdate();
    }

    /**
     * Handle creating a new diagram from the Browser.
     * Creates the diagram in the specified BC but does NOT navigate to it.
     * @param name - Name for the new diagram
     * @param contextId - The BC where the diagram should be created
     */
    handleCreateDiagramFromBrowser = (name: string, contextId: string) => {
        // Get or load the target workspace
        const targetWorkspace = this.getOrLoadBoundedContext(contextId);
        if (!targetWorkspace) {
            console.error(`Cannot create diagram: workspace not found for context ${contextId}`);
            return;
        }

        // Debug: Log datasets in the BC at diagram creation time
        const bcDatasets = targetWorkspace.getDatasets();
        console.debug(`handleCreateDiagramFromBrowser: BC ${contextId} has ${bcDatasets.length} datasets:`, bcDatasets.map(d => d.name));

        // Create a new diagram with the given name
        const newDiagramId = crypto.randomUUID();
        const diagram = new Diagram(name, contextId, newDiagramId);
        diagram.datasetId = null;
        targetWorkspace.registerView(diagram);

        // Add to workspace config for persistence
        addViewToConfig(this.props.workspaceConfig, contextId, newDiagramId, name, 'Diagram');

        console.debug(`Created new Diagram: ${name} (${newDiagramId}) in context ${contextId}`);

        // Do NOT navigate - stay on current view (per user requirement)
        this.forceUpdate();
    }

    /**
     * Handle vocabulary selection from Browser - switches to concept scheme view.
     * Updates URL without causing a full navigation/reload.
     * @param vocabularyUri - URI of the selected vocabulary
     * @param _vocabularyLabel - Display label of the vocabulary (for future use)
     */
    handleVocabularySelected = async (vocabularyUri: string, _vocabularyLabel: string) => {
        // Update URL without navigation (pushState doesn't trigger React Router remount)
        const newUrl = `/vocabularies/${encodeURIComponent(vocabularyUri)}`;
        window.history.pushState({ vocabularyUri }, '', newUrl);

        // Update state to show concept scheme view
        this.setState({
            viewMode: 'concept-scheme',
            selectedElement: null,
            selectedVocabularyUri: vocabularyUri
        });

        // Load vocabulary if needed
        await this.loadVocabularyIfNeeded(vocabularyUri);
    }

    /** Proxy URL for CORS-blocked requests */
    private readonly PROXY_URL = 'http://localhost:3001/proxy';

    /**
     * Load vocabulary from TTL file or remote IRI if not already loaded.
     * Called when vocabularyId prop is set (from URL).
     */
    loadVocabularyIfNeeded = async (vocabularyUri: string) => {
        // Find the bounded context that contains this vocabulary
        const contextId = findContextIdForVocabulary(this.props.workspaceConfig, vocabularyUri);
        if (!contextId) {
            console.error('No bounded context found for vocabulary:', vocabularyUri);
            return;
        }

        // Get or load the bounded context
        const bc = this.getOrLoadBoundedContext(contextId);
        if (!bc) {
            console.error('Failed to load bounded context:', contextId);
            return;
        }

        // Get the vocabulary ref from the bounded context
        const vocabRef = bc.getUbiquitousLanguage()?.getVocabularyByUri(vocabularyUri);

        // Skip if already loaded
        if (vocabRef?.vocabulary) {
            return;
        }

        if (vocabRef) {
            if (vocabRef.ttlPath) {
                // Local vocabulary: load from TTL path
                await this.loadVocabularyFromPath(vocabRef);
            } else if (vocabRef.source === 'imported') {
                // Imported vocabulary: fetch from IRI with content negotiation
                await this.loadVocabularyFromIri(vocabRef);
            }
        }
    }

    /**
     * Load vocabulary from a local TTL file path.
     */
    private loadVocabularyFromPath = async (vocabRef: VocabularyRef) => {
        try {
            console.debug('Loading vocabulary from TTL:', vocabRef.ttlPath);
            const response = await fetch(vocabRef.ttlPath!);
            if (response.ok) {
                const ttlContent = await response.text();
                const loadedVocabulary = await Vocabulary.fromTurtle(ttlContent);
                // Store the loaded vocabulary on the ref (mutable property)
                (vocabRef as { vocabulary?: Vocabulary }).vocabulary = loadedVocabulary;
                console.debug('Vocabulary loaded:', loadedVocabulary.getConcepts().length, 'concepts');
                // Force re-render to show loaded vocabulary
                this.forceUpdate();
            } else {
                console.error('Failed to fetch vocabulary TTL:', response.status);
            }
        } catch (error) {
            console.error('Error loading vocabulary:', error);
        }
    }

    /**
     * Load vocabulary from a remote IRI or download URL.
     * Falls back to proxy if direct fetch fails due to CORS.
     */
    private loadVocabularyFromIri = async (vocabRef: VocabularyRef) => {
        // Use downloadUrl if specified, otherwise fall back to IRI
        const fetchUrl = vocabRef.downloadUrl || vocabRef.uri;
        console.debug('Fetching vocabulary from:', fetchUrl, vocabRef.downloadUrl ? '(downloadUrl)' : '(IRI)');

        try {
            // Try direct fetch first
            let response: Response;
            try {
                response = await this.tryFetchVocabulary(fetchUrl, false);
            } catch {
                // Network error (likely CORS), try proxy
                console.debug('Direct fetch failed (CORS), trying proxy...');
                response = await this.tryFetchVocabulary(fetchUrl, true);
            }

            if (!response.ok) {
                console.error('Failed to fetch vocabulary:', response.status, response.statusText);
                return;
            }

            const content = await response.text();
            console.debug('Vocabulary content size:', content.length, 'bytes');
            const loadedVocabulary = await Vocabulary.fromTurtle(content);

            // Store on ref
            (vocabRef as { vocabulary?: Vocabulary }).vocabulary = loadedVocabulary;
            console.debug('Vocabulary loaded from IRI:', loadedVocabulary.getConcepts().length, 'concepts');
            this.forceUpdate();
        } catch (error) {
            console.error('Error loading vocabulary from IRI:', error);
        }
    }

    /**
     * Fetch vocabulary content, optionally using proxy.
     */
    private tryFetchVocabulary = async (uri: string, useProxy: boolean): Promise<Response> => {
        const fetchUrl = useProxy
            ? `${this.PROXY_URL}?url=${encodeURIComponent(uri)}`
            : uri;

        return fetch(fetchUrl, {
            headers: {
                'Accept': 'text/turtle, application/rdf+xml;q=0.9, */*'
            }
        });
    }

    /**
     * Get a class label by URI from the loaded class taxonomy.
     * Recursively searches the taxonomy tree.
     */
    getClassLabelFromTaxonomy = (uri: string): string | undefined => {
        return getClassLabelFromTaxonomy(this.state.classTaxonomy, uri);
    }

    applyTheme(darkMode: boolean) {
        // Toggle Tailwind dark mode class FIRST (so CSS variables update)
        document.documentElement.classList.toggle('dark', darkMode);

        // Now read theme values (CSS variables are now correct)
        const currentTheme = getTheme();

        // Apply theme to body background
        document.body.style.backgroundColor = currentTheme.background;
    }

    handleDarkModeToggle = (newDarkMode?: boolean) => {
        const darkMode = newDarkMode !== undefined ? newDarkMode : !this.state.darkMode;
        this.applyTheme(darkMode);
        this.setState({ darkMode });
    }

    getStatusText(): string {
        const { hoveredBrowserElement, hoveredToolboxClass } = this.state;
        if (hoveredBrowserElement) {
            if (isNode(hoveredBrowserElement)) {
                return hoveredBrowserElement.definition;
            } else {
                const rel = hoveredBrowserElement as Relationship;
                return `${rel.subject.name} → ${rel.name} → ${rel.object.name}`;
            }
        }
        return hoveredToolboxClass?.description || '';
    }

    /**
     * Resolve an ObjectProperty from the relationship taxonomy by URI.
     * Returns the full ObjectProperty with visual annotations (sourceMarker, targetMarker, etc.)
     */
    resolveObjectProperty = (uri: string): ObjectProperty => {
        const findNode = (nodes: RelationshipTaxonomyNode[]): RelationshipTaxonomyNode | undefined => {
            for (const node of nodes) {
                if (node.uri === uri) return node;
                if (node.children) {
                    const found = findNode(node.children);
                    if (found) return found;
                }
            }
            return undefined;
        };

        const node = this.state.relationshipTaxonomy ? findNode(this.state.relationshipTaxonomy) : undefined;

        if (node) {
            return {
                uri: node.uri || uri,
                label: node.label,
                relationshipType: node.type,
                strokeStyle: node.strokeStyle,
                sourceMarker: node.sourceMarker,
                targetMarker: node.targetMarker,
                domain: node.domain,
                range: node.range
            };
        }

        // Fallback: return minimal property with label extracted from URI
        const hashIndex = uri.lastIndexOf('#');
        const slashIndex = uri.lastIndexOf('/');
        const label = hashIndex !== -1
            ? uri.substring(hashIndex + 1)
            : (slashIndex !== -1 ? uri.substring(slashIndex + 1) : uri);
        return { uri, label };
    }

    /**
     * Run inference engine and create inferred relationships.
     * Converts InferredRelationshipData to actual DiagramRelationship instances.
     */
    runInference = () => {
        const diagram = this.diagram;
        const knowledgeGraph = this.knowledgeGraph;
        const bc = this.boundedContext;
        if (!diagram || !knowledgeGraph || !bc) return;

        console.debug("Running inference engine...");

        // Run the inference engine
        const inferredData = this.inferenceEngine.runInference(
            knowledgeGraph,
            diagram
        );

        if (inferredData.length === 0) {
            console.debug("No relationships inferred");
            bc.clearInferredKnowledgeGraph();
            diagram.clearInferredRelationships();
            this.notifyDiagramChanged();
            return;
        }

        // Create a new KnowledgeGraph for inferred relationships
        const inferredKG = new KnowledgeGraph("inferred", "urn:inferred:");

        // Convert inferred data to DiagramRelationships
        const inferredRelationships: DiagramRelationship[] = [];

        // Build a map of element URI -> DiagramClass for quick lookup
        const diagramClassMap = new Map<string, DiagramClass>();
        for (const dc of diagram.classes) {
            diagramClassMap.set(dc.modelElement.uri, dc);
        }

        for (const data of inferredData) {
            const sourceDiagramClass = diagramClassMap.get(data.subjectUri);
            const targetDiagramClass = diagramClassMap.get(data.objectUri);

            if (!sourceDiagramClass || !targetDiagramClass) {
                console.debug(`Skipping inferred relationship: missing diagram class for ${data.subjectUri} or ${data.objectUri}`);
                continue;
            }

            // Resolve the full ObjectProperty with visual annotations from taxonomy
            const predicate = this.resolveObjectProperty(data.predicateUri);
            console.debug('Inferred relationship predicate:', {
                uri: predicate.uri,
                label: predicate.label,
                sourceMarker: predicate.sourceMarker,
                targetMarker: predicate.targetMarker,
                subjectUri: data.subjectUri,
                objectUri: data.objectUri
            });

            // Create Relationship with isInferred=true
            const relationship = new Relationship(
                sourceDiagramClass.modelElement,
                predicate,
                targetDiagramClass.modelElement,
                `inferred:${data.ruleId}:${data.subjectUri}:${data.objectUri}`,
                true  // isInferred
            );

            // Add to inferred KnowledgeGraph
            inferredKG.addRelationship(relationship);

            // Create DiagramRelationship
            const diagramRel = new DiagramRelationship(
                relationship,
                sourceDiagramClass,
                targetDiagramClass
            );

            inferredRelationships.push(diagramRel);
        }

        console.debug(`Created ${inferredRelationships.length} inferred relationships`);

        // Store inferred KnowledgeGraph in Workspace
        bc.setInferredKnowledgeGraph(inferredKG);

        diagram.setInferredRelationships(inferredRelationships);
        this.notifyDiagramChanged();
    }

    /**
     * Clear all inferred relationships from the diagram and Workspace.
     */
    clearInferredRelationships = () => {
        console.debug("Clearing inferred relationships");
        this.boundedContext?.clearInferredKnowledgeGraph();
        this.diagram?.clearInferredRelationships();
        this.notifyDiagramChanged();
    }

    /**
     * Run inference if enabled. Call this after model changes.
     */
    runInferenceIfEnabled = () => {
        if (this.diagram?.inferenceEnabled) {
            this.runInference();
        }
    }

    /**
     * Add DiagramRelationships for any existing KG relationships involving a DiagramClass.
     * Call this after adding a DiagramClass to the diagram to connect it to existing elements.
     */
    addExistingRelationshipsForDiagramClass = (diagramClass: DiagramClass) => {
        const diagram = this.diagram;
        const knowledgeGraph = this.knowledgeGraph;
        if (!diagram || !knowledgeGraph) return;

        const individualUri = diagramClass.modelElement.uri;
        const newDiagramRelationships: DiagramRelationship[] = [];

        for (const relationship of knowledgeGraph.relationships.values()) {
            // Check if this individual is the object of a relationship
            if (relationship.object.uri === individualUri) {
                const sourceDiagramClass = diagram.classes.find(
                    dc => dc.modelElement.uri === relationship.subject.uri
                );
                if (sourceDiagramClass) {
                    const existingDiagramRel = diagram.relationships.find(
                        dr => dr.modelRelationship === relationship
                    );
                    if (!existingDiagramRel) {
                        newDiagramRelationships.push(
                            new DiagramRelationship(relationship, sourceDiagramClass, diagramClass)
                        );
                    }
                }
            }
            // Check if this individual is the subject of a relationship
            if (relationship.subject.uri === individualUri) {
                const targetDiagramClass = diagram.classes.find(
                    dc => dc.modelElement.uri === relationship.object.uri
                );
                if (targetDiagramClass) {
                    const existingDiagramRel = diagram.relationships.find(
                        dr => dr.modelRelationship === relationship
                    );
                    if (!existingDiagramRel) {
                        newDiagramRelationships.push(
                            new DiagramRelationship(relationship, diagramClass, targetDiagramClass)
                        );
                    }
                }
            }
        }

        for (const diagramRel of newDiagramRelationships) {
            diagram.addRelationship(diagramRel);
        }

        if (newDiagramRelationships.length > 0) {
            console.log(`Added ${newDiagramRelationships.length} existing relationship(s) to diagram`);
        }
    }

    handleDiagramClassAdded = (diagramClass: DiagramClass) => {
        console.log("Class added: " + diagramClass.modelElement.name);
        console.debug({diagramClass});
        // Add individual to knowledge graph (ABox only for now)
        if (diagramClass.modelElement instanceof Individual) {
            // Get ontology info from the current diagram's modeling language
            const modelingLanguageIri = this.diagram?.modelingLanguageIri;
            const ontologySchema = modelingLanguageIri
                ? this.boundedContext?.getOntologyByIRI(modelingLanguageIri)
                : undefined;

            // Build ontology info including namespace and prefix for KG tracking
            const namespace = ontologySchema?.getNamespace();
            const prefix = namespace ? ontologySchema?.prefixes.get(namespace) : undefined;

            const ontologyInfo = ontologySchema ? {
                ontologyIri: ontologySchema.ontologyIRI,
                versionIri: ontologySchema.versionIRI,
                namespace,
                prefix
            } : undefined;

            this.knowledgeGraph!.addIndividual(diagramClass.modelElement, ontologyInfo);
        }
        // Note: TBox editing (SchemaClass) is deferred to future implementation
        this.diagram!.addClass(diagramClass);

        // Add any existing relationships involving this element
        this.addExistingRelationshipsForDiagramClass(diagramClass);

        this.notifyDiagramChanged(this.runInferenceIfEnabled);
    }

    /**
     * Handle adding a SchemaIndividual (TBox individual) from the Browser to the Diagram.
     * SchemaIndividuals are not added to the KnowledgeGraph - they belong to the ontology.
     */
    handleAddSchemaIndividualToDiagram = (schemaIndividual: SchemaIndividual) => {
        // Find which ontology this individual belongs to
        const sourceGraphIRI = this.boundedContext?.getOntologyForIndividual(schemaIndividual.uri)?.ontologyIRI;

        const diagram = this.diagram;
        if (!diagram) return;

        // Calculate position (center of viewport)
        const svgEl = this.diagramEditor.current?.svg.current;
        const centerX = svgEl ? svgEl.clientWidth / 2 : 400;
        const centerY = svgEl ? svgEl.clientHeight / 2 : 300;

        // Account for current viewport transform
        const vt = diagram.viewportTransform;
        const x = (centerX - vt.x) / vt.k;
        const y = (centerY - vt.y) / vt.k;

        // Get namespace tag prefix for width calculation
        const namespaceTag = sourceGraphIRI ? this.boundedContext?.getNamespaceTag(sourceGraphIRI) : undefined;

        // Calculate dimensions (pass class label resolver for correct stereotype width)
        const { width, height } = DiagramClassElement.calculateDimensions(
            schemaIndividual,
            true,
            true,
            this.getClassLabelFromTaxonomy,
            namespaceTag?.prefix
        );

        // Create DiagramClass with source graph tracking
        // SchemaIndividual implements the Individual interface, so DiagramEditor treats it uniformly
        const diagramClass = new DiagramClass(
            schemaIndividual,
            x - width / 2,
            y - height / 2,
            width,
            height,
            false,
            sourceGraphIRI
        );

        // Add to diagram only - SchemaIndividuals are NOT added to KnowledgeGraph (they belong to ontology)
        diagram.addClass(diagramClass);

        // Add any existing relationships involving this element
        this.addExistingRelationshipsForDiagramClass(diagramClass);

        this.notifyDiagramChanged(this.runInferenceIfEnabled);
    }

    handleDiagramClassMoved = (diagramClass: DiagramClass, x: number, y: number) => {
        diagramClass.x = x;
        diagramClass.y = y;
        this.notifyDiagramChanged();
    }

    handleDiagramChanged = () => {
        // Trigger state update to persist diagram changes (e.g., anchor positions)
        this.notifyDiagramChanged();
    }

    handleRelationshipReconnected = (rel: DiagramRelationship, isSource: boolean, newClass: DiagramClass) => {
        // Log the reconnection for debugging
        const endpointName = isSource ? 'source' : 'target';
        console.log(`Relationship ${rel.modelRelationship.name} ${endpointName} reconnected to ${newClass.modelElement.name}`);
        // Trigger state update to persist the change
        this.notifyDiagramChanged(this.runInferenceIfEnabled);
    }

    handleDiagramRelationshipAdded = (rel: DiagramRelationship) => {
        console.log("Relationship added: " + rel);
        console.debug({rel});
        this.knowledgeGraph!.addRelationship(rel.modelRelationship);
        this.diagram!.addRelationship(rel);
        this.notifyDiagramChanged(this.runInferenceIfEnabled);
    }

    handleDiagramClassDeleted = (diagramClass: DiagramClass) => {
        console.log("Class deleted: " + diagramClass.modelElement.name);

        const modelElement = diagramClass.modelElement;
        const isInKnowledgeGraph = this.knowledgeGraph?.getNodeByUri(modelElement.uri) !== undefined;

        if (isInKnowledgeGraph && modelElement instanceof Individual) {
            // Individual is in the KnowledgeGraph - remove it and its relationships from KG
            this.knowledgeGraph!.removeIndividual(modelElement);
        }
        // Note: If individual is NOT in the KG (e.g., TBox individual from ontology),
        // we only remove from diagram. The KG relationships are handled by handleDiagramRelationshipDeleted
        // which is called first by DiagramEditor for each connected relationship.

        // Note: TBox editing (SchemaClass) is deferred to future implementation
        this.diagram!.removeClass(diagramClass);
        this.setState({ selectedElement: null });
        this.notifyDiagramChanged(this.runInferenceIfEnabled);
    }

    handleDiagramRelationshipDeleted = (rel: DiagramRelationship) => {
        console.log("Relationship deleted: " + rel.modelRelationship.name);

        // Only delete the KG relationship if BOTH endpoints are in the KnowledgeGraph
        // If either endpoint is external (e.g., TBox individual from ontology), keep the KG relationship
        const subjectInKG = this.knowledgeGraph?.getNodeByUri(rel.modelRelationship.subject.uri) !== undefined;
        const objectInKG = this.knowledgeGraph?.getNodeByUri(rel.modelRelationship.object.uri) !== undefined;

        if (subjectInKG && objectInKG) {
            this.knowledgeGraph!.removeRelationship(rel.modelRelationship);
        } else {
            console.debug("Keeping KG relationship - one or more endpoints are external to the KG");
        }

        this.diagram!.removeRelationship(rel);
        this.notifyDiagramChanged(this.runInferenceIfEnabled);
    }

    handleDiagramClassHidden = (diagramClass: DiagramClass) => {
        console.log("Class hidden: " + diagramClass.modelElement.name);
        // Mark the class as hidden (it stays in the model, just not rendered)
        diagramClass.hidden = true;
        // Trigger re-render and persistence
        this.setState({ selectedElement: null });
        this.notifyDiagramChanged();
    }

    handleUnhideAll = () => {
        console.log("Unhiding all hidden classes");
        this.diagram?.unhideAll();
        this.notifyDiagramChanged();
    }

    handleDiagramClassSelected = (diagramClass: DiagramClass | null) => {
        this.selectElement((diagramClass) ? diagramClass?.modelElement : null);
    }

    handleDiagramRelationshipSelected = (diagramRelationship: DiagramRelationship | null) => {
        this.selectElement((diagramRelationship) ? diagramRelationship.modelRelationship : null);
    }

    handleConceptSelected = (concept: Concept) => {
        this.selectConcept(concept);
    }

    /**
     * Generate a unique individual name based on existing individuals in the model.
     * Pattern: "My {classLabel}", "My {classLabel} 2", "My {classLabel} 3", etc.
     */
    generateUniqueIndividualName(classLabel: string): string {
        const baseName = `My ${classLabel}`;
        const existingNames = new Set(
            Array.from(this.knowledgeGraph?.individuals.values() ?? []).map(ind => ind.name)
        );

        // Check if base name is available
        if (!existingNames.has(baseName)) {
            return baseName;
        }

        // Find the next available suffix by checking existing names
        let suffix = 2;
        while (existingNames.has(`${baseName} ${suffix}`)) {
            suffix++;
        }
        return `${baseName} ${suffix}`;
    }

    handleToolboxClassSelected = (node: ClassTaxonomyNode) => {
        console.debug("Toolbox class selected:", node);
        this.setState({ toolboxSelectedClass: node });

        // Start dragging mode with the selected class type
        if (!this.knowledgeGraph) {
            console.warn('handleToolboxClassSelected: No KnowledgeGraph available. Diagram may not have an associated dataset.');
            return;
        }
        if (node.classType && this.diagramEditor.current) {
            // Remove any existing placeholder before creating a new one
            this.diagramEditor.current.stopDrag();

            // Get data properties for this class from the loaded map
            const dataProperties = this.state.dataPropertiesMap?.get(node.uri);
            // Get subClassOf axioms (ObjectPropertyRestrictions) for this class
            const subClassOf = this.state.objectPropertyRestrictionsMap?.get(node.uri);
            console.debug(`handleToolboxClassSelected: classUri=${node.uri}, dataPropsMapSize=${this.state.dataPropertiesMap?.size}, hasDataProps=${!!dataProperties}, dataPropsCount=${dataProperties?.length}, hasSubClassOf=${!!subClassOf}, subClassOfCount=${subClassOf?.length}`);

            // Generate a unique name for this individual
            const uniqueName = this.generateUniqueIndividualName(node.label);

            // Create a temporary concept for the individual
            const concept = new Concept(
                `urn:temp:${Date.now()}`,
                uniqueName,
                [],
                [],
                node.description || ""
            );

            // Create a placeholder concept for the TBox class
            const classConcept = new Concept(node.uri, node.label, [], [], "");

            // Create the SchemaClass reference (TBox)
            const schemaClass = new SchemaClass('class', classConcept, {
                label: node.label,
                stereotype: node.stereotype,
                categoryColor: node.categoryColor,
                dataProperties: dataProperties,
                subClassOf: subClassOf
            });

            // Create Individual (ABox) - instance of the SchemaClass
            const individualUri = this.knowledgeGraph.generateIndividualUri(uniqueName);
            const individual = new Individual(
                concept,
                individualUri,
                uniqueName,
                schemaClass
            );

            // Show class label in placeholder (e.g. «Belgian Citizen») - consistent with ABox display
            const stereotypeDisplay = `«${node.label}»`;

            this.diagramEditor.current.dragActive = true;
            this.diagramEditor.current.dragActiveElement = this.diagramEditor.current.createClassPlaceholder(stereotypeDisplay);
            this.diagramEditor.current.dragActiveModel = individual;
        }
    }

    handleToolboxClassHovered = (node: ClassTaxonomyNode | null) => {
        this.setState({ hoveredToolboxClass: node });
    };

    handleBrowserElementHovered = (element: Element | null) => {
        this.setState({ hoveredBrowserElement: element });
    };

    handleBrowserWidthChange = (width: number) => {
        this.setState({ browserWidth: width });
    };

    handlePredicateToggle = (predicateUri: string, color: string) => {
        this.setState(prevState => {
            const newMap = new Map(prevState.highlightedPredicates);
            if (newMap.has(predicateUri)) {
                newMap.delete(predicateUri);
            } else {
                newMap.set(predicateUri, color);
            }
            return { highlightedPredicates: newMap };
        });
    };

    handleClearAllPredicateHighlights = () => {
        this.setState({ highlightedPredicates: new Map() });
    };

    handleNamespaceColorChange = (namespace: string, color: string) => {
        this.boundedContext?.setNamespaceColor(namespace, color);
        const allColors = this.boundedContext?.getAllNamespaceColors();
        if (allColors) {
            this.persistenceService.saveNamespaceColors(allColors);
        }

        // Force re-render of all components
        this.setState({ namespaceColorsVersion: this.state.namespaceColorsVersion + 1 });
    };

    getNamespaceColor = (namespace: string): string | undefined => {
        // Try exact match first
        let color = this.boundedContext?.getNamespaceColor(namespace);
        if (color) return color;

        // Try with # appended/removed
        const withHash = namespace.endsWith('#') ? namespace.slice(0, -1) : namespace + '#';
        color = this.boundedContext?.getNamespaceColor(withHash);
        if (color) return color;

        // Try with / appended/removed
        const withSlash = namespace.endsWith('/') ? namespace.slice(0, -1) : namespace + '/';
        color = this.boundedContext?.getNamespaceColor(withSlash);
        if (color) return color;

        return undefined;
    };

    render() {
        const selectedElement = this.state.selectedElement;
        const bc = this.boundedContext;
        const datasets = bc?.getDatasets() ?? [];

        // noinspection BadExpressionStatementJS
        return (
            <div id="app" className="flex flex-col h-full w-full">
                <TitleBar
                    knowledgeGraphUri={this.knowledgeGraph?.uri ?? ''}
                    editingMode={this.diagram?.editingMode ?? 'ABox'}
                    showChat={this.state.showChat}
                    darkMode={this.state.darkMode}
                    addingInline={this.state.addingInline}
                    draftTopConceptName={this.state.draftTopConceptName}
                    proposedConcepts={this.state.proposedConcepts}
                    vocabulary={this.props.vocabulary}
                    selectedModelingLanguageIri={this.diagram?.modelingLanguageIri ?? null}
                    kgOntologies={this.getKgOntologiesForDropdown()}
                    contextOntologies={bc ? [
                        ...bc.getModel().getImportedOntologies(),
                        ...bc.getModel().getLocalOntologies()
                    ] : []}
                    domainOntologies={this.getDomainOntologiesForDropdown()}
                    onModelingLanguageChange={this.handleModelingLanguageChange}
                    selectedLabelingContextIri={this.diagram?.labelingContextIri ?? null}
                    availableLabelingContexts={bc?.getUbiquitousLanguage().getAllVocabularies() ?? []}
                    onLabelingContextChange={this.handleLabelingContextChange}
                    selectedDatasetId={this.diagram?.datasetId ?? null}
                    availableDatasets={datasets}
                    onDatasetChange={this.handleDatasetChange}
                    draggingConceptUri={this.state.draggingConceptUri}
                    onToggleView={() => this.toggleView()}
                    onOpenVocabularyTurtleModal={() => this.openVocabularyTurtleModal()}
                    onOpenDomainModelTurtleModal={() => this.openDomainModelTurtleModal()}
                    onOpenDiagramTurtleModal={() => this.openDiagramTurtleModal()}
                    onExportToJson={() => this.exportToJson()}
                    onExportToTurtle={() => this.exportToTurtle()}
                    onCreateNewDiagram={() => this.createNewDiagram()}
                    onShowExamplePicker={() => this.showExamplePicker()}
                    onToggleChat={() => this.setState({ showChat: !this.state.showChat })}
                    onSetEditingMode={(mode) => { if (this.diagram) { this.diagram.editingMode = mode; this.forceUpdate(); } }}
                    onStartInlineAdd={() => this.startInlineAdd()}
                    onConfirmInlineAdd={() => this.confirmInlineAdd()}
                    onCancelInlineAdd={() => this.cancelInlineAdd()}
                    onDraftTopConceptNameChange={(name) => this.setState({ draftTopConceptName: name })}
                    onAddConcept={(concept) => this.addConcept(concept)}
                    onRejectConcept={(concept) => this.rejectConcept(concept)}
                    onDropConceptClass={(uri) => this.dropConceptClass(uri)}
                    hasConceptInDiagram={(uri) => this.hasConceptInDiagram(uri)}
                    onOpenBoundedContextInfo={() => this.openBoundedContextInfoModal()}
                    hasExamples={(() => {
                        const domain = this.getModelingLanguageDomain();
                        return !!domain?.exampleTtl && Object.keys(domain.exampleTtl).length > 0;
                    })()}
                />

                <div id="main" className="flex-1 flex min-h-0">
                    <div id="browserContainer" className="shrink-0 h-full overflow-visible relative z-[100]">
                        <Browser
                            rootDomains={this.props.workspace.getRootDomains()}
                            boundedContext={this.boundedContext}
                            knowledgeGraph={this.knowledgeGraph ?? null}
                            schemaIndividuals={this.getSchemaIndividuals()}
                            selectedElement={this.state.selectedElement}
                            onElementSelected={this.selectElement}
                            onElementHovered={this.handleBrowserElementHovered}
                            onElementRenamed={this.handleElementRenamed}
                            onAddSchemaIndividualToDiagram={this.handleAddSchemaIndividualToDiagram}
                            editingMode={this.diagram?.editingMode ?? 'ABox'}
                            darkMode={this.state.darkMode}
                            browserWidth={this.state.browserWidth}
                            onBrowserWidthChange={this.handleBrowserWidthChange}
                            activeDiagramId={this.props.diagramId}
                            onDiagramSelected={(diagramId) => {
                                // Clear vocabulary selection and switch to diagram view
                                this.setState({ selectedVocabularyUri: null, viewMode: 'diagram' });
                                if (this.props.navigateToDiagram) {
                                    this.props.navigateToDiagram(diagramId);
                                } else {
                                    window.location.href = `/diagrams/${diagramId}`;
                                }
                            }}
                            onCreateDataset={this.handleCreateDataset}
                            onCreateDiagram={this.handleCreateDiagramFromBrowser}
                            onVocabularySelected={this.handleVocabularySelected}
                            selectedVocabularyUri={this.state.selectedVocabularyUri}
                        />
                    </div>
                    {/* Toolbox - only show when in diagram view */}
                    {this.state.viewMode === 'diagram' && (
                        <div id="toolboxContainer" className="shrink-0 h-full overflow-visible relative z-[100]">
                            {this.state.classTaxonomy && (
                                <Toolbox
                                    taxonomy={this.state.classTaxonomy}
                                    domainPrefixes={this.state.domainPrefixes}
                                    getNamespaceColor={this.getNamespaceColor}
                                    onClassSelected={this.handleToolboxClassSelected}
                                    onClassHovered={this.handleToolboxClassHovered}
                                    darkMode={this.state.darkMode}
                                    persistenceService={this.persistenceService}
                                    boundedContext={this.boundedContext}
                                    activeDomainNs={this.state.activeDomain?.ns}
                                />
                            )}
                        </div>
                    )}
                    <div id="editorContainer" className="relative flex-1 min-w-0 h-full">
                        {this.state.viewMode === 'concept-scheme' ? (
                            <ConceptSchemeViewer
                                vocabulary={this.selectedVocabulary}
                                onConceptSelected={this.handleConceptSelected}
                                selectedConceptUri={isNode(this.state.selectedElement) ? this.state.selectedElement.uri : null}
                            />
                        ) : !this.props.diagramId ? (
                            /* Empty state when no diagram is selected */
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <div className="text-slate-400 dark:text-white/40 text-lg mb-2">
                                        No diagram selected
                                    </div>
                                    <div className="text-slate-300 dark:text-white/25 text-sm">
                                        Select a diagram from the Browser to begin
                                    </div>
                                </div>
                            </div>
                        ) : (this.state.viewMode === 'diagram' && this.diagram) ? (
                            <DiagramEditor
                                ref={this.diagramEditor}
                                selectedElement={this.state.selectedElement}
                                onClassSelected={this.handleDiagramClassSelected}
                                onRelationshipSelected={this.handleDiagramRelationshipSelected}
                                gridSize={25}
                                diagram={this.diagram}
                                onClassAdded={(dc) => this.handleDiagramClassAdded(dc)}
                                onClassMoved={(dc, x, y) => this.handleDiagramClassMoved(dc, x, y)}
                                onRelationshipAdded={(dr) => this.handleDiagramRelationshipAdded(dr)}
                                onRelationshipReconnected={this.handleRelationshipReconnected}
                                onClassDeleted={(dc) => this.handleDiagramClassDeleted(dc)}
                                onRelationshipDeleted={(dr) => this.handleDiagramRelationshipDeleted(dr)}
                                onClassHidden={(dc) => this.handleDiagramClassHidden(dc)}
                                onUnhideAll={() => this.handleUnhideAll()}
                                onPlacementCancelled={() => this.setState({ draggingConceptUri: null })}
                                onDiagramChanged={this.handleDiagramChanged}
                                onElementRenamed={this.handleElementRenamed}
                                editingMode={this.diagram.editingMode}
                                generateIndividualUri={(name) => this.knowledgeGraph!.generateIndividualUri(name)}
                                domainTtl={this.state.combinedTtl}
                                domainNs={this.state.activeDomain.ns}
                                allowedNamespaces={this.state.allowedNamespaces}
                                showOptionalProperties={this.diagram.showOptionalProperties}
                                showEssentialProperties={this.diagram.showEssentialProperties}
                                showStereotype={this.diagram.showStereotype}
                                darkMode={this.state.darkMode}
                                relationshipTaxonomy={this.state.relationshipTaxonomy ?? undefined}
                                classTaxonomy={this.state.classTaxonomy ?? undefined}
                                dataPropertiesMap={this.state.dataPropertiesMap ?? undefined}
                                objectPropertyRestrictionsMap={this.state.objectPropertyRestrictionsMap ?? undefined}
                                classHierarchy={this.state.classHierarchy ?? undefined}
                                highlightedPredicates={this.state.highlightedPredicates}
                                getClassColor={(uri) => this.boundedContext?.getClassColor(uri)}
                                getClassLabel={this.getClassLabelFromTaxonomy}
                                getNamespaceTag={(sourceGraphIRI) => this.boundedContext?.getNamespaceTag(sourceGraphIRI)}
                            />
                        ) : this.state.viewMode === 'visual-vocabulary' ? (
                            <VisualVocabulary
                                ref={this.visualVocabulary}
                                vocabulary={this.props.vocabulary}
                                onConceptSelected={this.handleConceptSelected}/>
                        ) : null}
                    </div>
                    {/* WordCloud - only show when in diagram view */}
                    {this.state.viewMode === 'diagram' && (
                        <div id="wordCloudContainer" className="shrink-0 h-full overflow-visible relative">
                            <WordCloud
                                knowledgeGraph={this.knowledgeGraph ?? null}
                                highlightedPredicates={this.state.highlightedPredicates}
                                onPredicateToggle={this.handlePredicateToggle}
                                onClearAll={this.handleClearAllPredicateHighlights}
                                darkMode={this.state.darkMode}
                            />
                        </div>
                    )}
                    <div id="propertiesContainer" className={clsx(
                        "shrink-0 h-full overflow-y-auto border-l transition-[flex-basis,padding] duration-300 ease-in-out font-['Inter',-apple-system,BlinkMacSystemFont,sans-serif] text-xs",
                        "bg-white text-slate-700 border-slate-200 shadow-[-2px_0_8px_rgba(100,120,140,0.06)]",
                        "dark:bg-surface-dark dark:text-[#e0e0e0] dark:border-accent-border dark:shadow-none",
                        selectedElement ? "basis-[280px] py-3 px-3.5" : "basis-0 p-0 overflow-hidden"
                    )}>
                        <Properties
                            modelElement={isNode(selectedElement) ? selectedElement : null}
                            onDataPropertyValueChange={(individual, propertyUri, value) => {
                                this.handleIndividualPropertyValueChange(individual, propertyUri, value);
                            }}
                            knowledgeGraph={this.knowledgeGraph}
                            onObjectPropertyValueChange={this.handleObjectPropertyValueChange}
                            onObjectPropertyValueRemove={this.handleObjectPropertyValueRemove}
                            onExportEssentialTTL={this.openEssentialTurtleModal}
                            onNameChange={this.handleElementRenamed}
                            getIndividualsByClass={(classUri) => this.boundedContext?.getIndividualsByClass(classUri) ?? []}
                            getIndividualByUri={(uri) => this.boundedContext?.getIndividualByUri(uri)}
                        />
                    </div>
                </div>

                <StatusBar statusText={this.getStatusText()}>
                    <ToggleButton
                        label="Stereotype"
                        value={this.diagram?.showStereotype ?? true}
                        activeValue={true}
                        inactiveValue={false}
                        onChange={(v) => { if (this.diagram) { this.diagram.showStereotype = v; this.forceUpdate(); } }}
                    />
                    <ToggleButton
                        label="Optional"
                        value={this.diagram?.showOptionalProperties ?? true}
                        activeValue={true}
                        inactiveValue={false}
                        onChange={(v) => { if (this.diagram) { this.diagram.showOptionalProperties = v; this.forceUpdate(); } }}
                    />
                    <ToggleButton
                        label="Essential"
                        value={this.diagram?.showEssentialProperties ?? false}
                        activeValue={true}
                        inactiveValue={false}
                        onChange={(v) => { if (this.diagram) { this.diagram.showEssentialProperties = v; this.forceUpdate(); } }}
                    />
                    <ToggleButton
                        label="Inference"
                        value={this.diagram?.inferenceEnabled ?? false}
                        activeValue={true}
                        inactiveValue={false}
                        onChange={(v) => {
                            if (this.diagram) {
                                this.diagram.inferenceEnabled = v;
                                if (v) {
                                    this.runInference();
                                } else {
                                    this.clearInferredRelationships();
                                }
                                this.forceUpdate();
                            }
                        }}
                        title={this.diagram?.inferenceEnabled ? 'Disable relationship inference' : 'Enable relationship inference'}
                    />
                    <ToggleButton
                        label={this.state.darkMode ? '🌙' : '☀️'}
                        value={this.state.darkMode}
                        activeValue={true}
                        inactiveValue={false}
                        onChange={this.handleDarkModeToggle}
                        title={this.state.darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                    />
                </StatusBar>

                {this.state.showChat && this.knowledgeGraph && (
                    <Chat
                        onAdvice={(advice) => this.handleAdvice(advice)}
                        vocabulary={this.props.vocabulary}
                        model={this.knowledgeGraph}
                    />
                )}
                {this.state.showTurtleModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]">
                        <div className="bg-white dark:bg-surface-dark-elevated text-slate-700 dark:text-white p-5 rounded-lg w-[80%] max-w-[900px] shadow-[0_8px_32px_rgba(100,120,140,0.2)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.5)] border border-slate-200 dark:border-accent-border">
                            <h3 className="mt-0 text-slate-800 dark:text-white">{this.state.turtleModalTitle}</h3>
                            <textarea
                                readOnly
                                value={this.state.turtleText}
                                className="w-full h-[300px] bg-slate-50 dark:bg-[#202030] text-slate-700 dark:text-white border border-slate-200 dark:border-accent/30 rounded-md p-2.5 font-mono text-xs"
                            />
                            <div className="mt-2.5 text-right">
                                <button
                                    onClick={() => this.downloadTurtle()}
                                    className="bg-accent-teal dark:bg-accent-muted text-white dark:text-accent/90 border-none dark:border dark:border-accent/30 px-4 py-2 rounded-md cursor-pointer font-medium"
                                >Download .ttl</button>
                                <button
                                    onClick={() => this.closeTurtleModal()}
                                    className="ml-2 bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-white border border-slate-200 dark:border-white/20 px-4 py-2 rounded-md cursor-pointer font-medium"
                                >Close</button>
                            </div>
                        </div>
                    </div>
                )}
                {this.state.showBoundedContextInfoModal && this.boundedContext && (
                    <BoundedContextInfoModal
                        boundedContext={this.boundedContext}
                        activeDomain={this.state.activeDomain}
                        darkMode={this.state.darkMode}
                        onClose={() => this.closeBoundedContextInfoModal()}
                        onNamespaceColorChange={this.handleNamespaceColorChange}
                    />
                )}
                {this.state.showLoadUrlModal && (
                    <LoadUrlModal
                        darkMode={this.state.darkMode}
                        onLoad={this.handleLoadFromUrl}
                        onClose={() => this.closeLoadUrlModal()}
                    />
                )}
                <LoadingModal
                    isOpen={this.state.showLoadingModal}
                    statusText={this.state.loadingStatus}
                    darkMode={this.state.darkMode}
                />
            </div>
        );
    }

    addConcept(concept: Concept) {
        console.log("Adding concept: " + concept.prefLabel);
        console.log({concept});

        this.props.vocabulary.addConcept(concept);
        this.setState({ proposedConcepts: this.state.proposedConcepts.filter(c => c.uri !== concept.uri) });

    }

    private inferBaseUriFromVocabulary(): string {
        return inferBaseUri(this.props.vocabulary.getConcepts());
    }

    addConceptByName() {
        const name = (this.state.newConceptName || '').trim();
        if (!name) return;
        const base = this.inferBaseUriFromVocabulary();
        const uri = base + slugifyForUriSegment(name);
        const concept = new Concept(uri, name, [], [], "");
        this.props.vocabulary.addConcept(concept);
        this.setState({ newConceptName: "" });
    }

    startInlineAdd() {
        this.setState({ addingInline: true, draftTopConceptName: "New concept" });
    }

    confirmInlineAdd() {
        const name = (this.state.draftTopConceptName || '').trim();
        if (!name) {
            this.setState({ addingInline: false, draftTopConceptName: '' });
            return;
        }
        const base = this.inferBaseUriFromVocabulary();
        const uri = base + slugifyForUriSegment(name);
        const concept = new Concept(uri, name, [], [], "");
        this.props.vocabulary.addConcept(concept);
        this.setState({ addingInline: false, draftTopConceptName: '' });
    }

    cancelInlineAdd() {
        this.setState({ addingInline: false, draftTopConceptName: '' });
    }

    rejectConcept(concept: Concept) {
        console.log("Rejecting concept: " + concept.prefLabel);
        this.setState({ proposedConcepts: this.state.proposedConcepts.filter(c => c.uri !== concept.uri) });
    }

    handleAdvice(advice: Advice) {
        console.log("Advice received");
        console.log({advice});
        if (advice) {
            if (advice.type == "conceptAdvice") {
                const conceptAdvice = advice as ConceptAdvice;
                conceptAdvice.actions.forEach(action => {
                    if (action.type === "newConcept") {
                        const newConceptAction = action as NewConceptAction;
                        const proposedConcept = newConceptAction.concept;
                        const concept = new Concept(
                            "http://example.org/automobile/" + proposedConcept.prefLabel,
                            proposedConcept.prefLabel,
                            proposedConcept.broaderConcepts.map((broader: string) => "http://example.org/automobile/" + encodeURIComponent(broader)),
                            proposedConcept.narrowerConcepts.map((narrower: string) => "http://example.org/automobile/" + encodeURIComponent(narrower)),
                            proposedConcept.definition);
                        this.state.proposedConcepts.push(concept);
                        this.setState(this.state);
                    } else if (action.type === "updateDefinition") {
                        console.log("Update definition action");
                        const updateDefinitionAction = action;
                        this.props.vocabulary.updateConceptDefinition(updateDefinitionAction.concept.href, updateDefinitionAction.definition);
                        this.setState(this.state);
                    }
                })
            }
        }
    }

    /**
     * Handle property value change for an individual.
     * For single-value properties: sets the value (replaces any existing).
     * For multi-value properties: the caller (Properties.tsx) already called addPropertyValue/removePropertyValue,
     * so we just trigger persistence. We detect this by checking if the value already exists.
     */
    handleIndividualPropertyValueChange = (individual: Individual, propertyUri: string, value: string | null) => {
        const currentValues = individual.getPropertyValues(propertyUri);

        // If value is null, delete all values
        if (value === null) {
            individual.deletePropertyValue(propertyUri);
        }
        // If value already exists in the array, caller already added it - just trigger persistence
        else if (!currentValues.includes(value)) {
            // Single-value case or value not yet added - set it
            individual.setPropertyValue(propertyUri, value);
        }
        // Otherwise: value already in array, caller used addPropertyValue - just trigger re-render/persistence

        // Trigger re-render and persistence
        this.notifyDiagramChanged();
    }

    /**
     * Handle adding an object property value for an individual.
     * Always creates a new relationship (supports multi-value properties).
     * Creates both Relationship (domain) and DiagramRelationship (visual).
     */
    handleObjectPropertyValueChange = (
        individual: Individual,
        restriction: RestrictedObjectProperty,
        targetIndividualUri: string | null
    ) => {
        const knowledgeGraph = this.knowledgeGraph;
        const diagram = this.diagram;
        if (!knowledgeGraph || !diagram) return;

        if (targetIndividualUri === null) {
            return; // Use handleObjectPropertyValueRemove for deletion
        }

        // Check if this relationship already exists (avoid duplicates)
        const existingRel = Array.from(knowledgeGraph.relationships.values()).find(
            (r: Relationship) => r.subject === individual &&
                 r.predicate.uri === restriction.uri &&
                 r.object.uri === targetIndividualUri
        );
        if (existingRel) {
            return; // Already exists, don't create duplicate
        }

        // Find target individual from workspace (searches both ABox and TBox)
        const targetElement = this.boundedContext?.getIndividualByUri(targetIndividualUri);

        if (targetElement) {
            // Resolve the full ObjectProperty with visual annotations from taxonomy
            const predicate = this.resolveObjectProperty(restriction.uri);
            const newRelationship = new Relationship(individual, predicate, targetElement);
            knowledgeGraph.addRelationship(newRelationship);

            // Find DiagramClass for source and target to create DiagramRelationship
            const sourceDiagramClass = diagram.classes.find(dc => dc.modelElement.uri === individual.uri);
            const targetDiagramClass = diagram.classes.find(dc => dc.modelElement.uri === targetIndividualUri);

            if (sourceDiagramClass && targetDiagramClass) {
                // Create and add DiagramRelationship
                const newDiagramRel = new DiagramRelationship(newRelationship, sourceDiagramClass, targetDiagramClass);
                diagram.addRelationship(newDiagramRel);
            }
        }

        // Trigger re-render and persistence
        this.notifyDiagramChanged(this.runInferenceIfEnabled);
    }

    /**
     * Handle removing an object property value for an individual.
     * Removes the specific relationship by target URI from both KnowledgeGraph and Diagram.
     */
    handleObjectPropertyValueRemove = (
        individual: Individual,
        restriction: RestrictedObjectProperty,
        targetIndividualUri: string
    ) => {
        const knowledgeGraph = this.knowledgeGraph;
        const diagram = this.diagram;
        if (!knowledgeGraph || !diagram) return;

        // Find the specific Relationship to remove
        const relToRemove = Array.from(knowledgeGraph.relationships.values()).find(
            (r: Relationship) => r.subject === individual &&
                 r.predicate.uri === restriction.uri &&
                 r.object.uri === targetIndividualUri
        );

        if (relToRemove) {
            // Find and remove the corresponding DiagramRelationship
            const diagramRelToRemove = diagram.relationships.find(
                dr => dr.modelRelationship === relToRemove
            );
            if (diagramRelToRemove) {
                diagram.removeRelationship(diagramRelToRemove);
            }

            // Remove from knowledge graph
            knowledgeGraph.removeRelationship(relToRemove);
        }

        // Trigger re-render and persistence
        this.notifyDiagramChanged(this.runInferenceIfEnabled);
    }

    selectElement = (element: Element | null) => {
        console.log("Selected element: " + (isNode(element) ? element?.name : element?.id));
        console.log({element});

        this.setState({selectedElement: element});
    }

    /**
     * Handle renaming an element (Individual, SchemaClass, or SchemaIndividual).
     * Updates the concept's prefLabel and triggers re-render/persistence.
     */
    handleElementRenamed = (element: Node, newName: string) => {
        console.log(`Renaming "${element.name}" to "${newName}"`);

        if (element instanceof Individual) {
            // Update Individual: both concept.prefLabel and individualLabel
            element.concept = new Concept(
                element.concept.uri,
                newName,
                element.concept.broader,
                element.concept.narrower,
                element.concept.definition
            );
            element.individualLabel = newName;
        } else if (element instanceof SchemaClass) {
            // Update SchemaClass: concept.prefLabel
            element.concept = new Concept(
                element.concept.uri,
                newName,
                element.concept.broader,
                element.concept.narrower,
                element.concept.definition
            );
        } else if (element instanceof SchemaIndividual) {
            // Update SchemaIndividual: concept.prefLabel
            element.concept = new Concept(
                element.concept.uri,
                newName,
                [],
                [],
                element.concept.definition
            );
        }

        // Trigger re-render and persistence
        this.notifyDiagramChanged();
    }

    /**
     * Get all SchemaIndividuals from all ontologies in the bc.
     * These are TBox-level individuals (e.g., Belgium, Male) defined in the ontology.
     */
    getSchemaIndividuals = (): SchemaIndividual[] => {
        return this.boundedContext?.getAllSchemaIndividuals() ?? [];
    }

    hasConceptInDiagram(_conceptUri: string): boolean {
        // For ABox mode, check if an individual with this class concept exists
        // Note: This is TBox-oriented and may need rework for ABox-only editing
        return false; // TBox editing deferred
    }

    dropConceptClass(conceptId: string) {
        // Note: This is TBox editing functionality - creating SchemaClass from concepts
        // This is deferred to future implementation (ABox focus for now)
        // Check if concept already exists in diagram
        if (this.hasConceptInDiagram(conceptId)) {
            const concept = this.props.vocabulary.getConcept(conceptId);
            alert(`"${concept.prefLabel}" is already in the diagram. Each concept can only be added once.`);
            return;
        }

        const concept = this.props.vocabulary.getConcept(conceptId!);
        const schemaClass = new SchemaClass("Entity", concept);

        this.diagramEditor.current!.dragActive = true;
        this.diagramEditor.current!.dragActiveElement = this.diagramEditor.current!.createClassPlaceholder(concept.prefLabel);
        this.diagramEditor.current!.dragActiveModel = schemaClass;

        this.setState({ draggingConceptUri: conceptId });
    }

    cancelConceptPlacement() {
        // Cancel any active drag (from concept chips or toolbox)
        this.diagramEditor.current?.stopDrag();
        if (this.state.draggingConceptUri) {
            this.setState({ draggingConceptUri: null });
        }
    }

    toggleView() {
        const modes: Array<'diagram' | 'visual-vocabulary' | 'concept-scheme'> = ['diagram', 'visual-vocabulary', 'concept-scheme'];
        const currentIndex = modes.indexOf(this.state.viewMode);
        const nextIndex = (currentIndex + 1) % modes.length;
        const nextMode = modes[nextIndex];
        this.setState({
            selectedElement: null,
            viewMode: nextMode,
            // Clear vocabulary selection when switching away from concept-scheme view
            selectedVocabularyUri: nextMode === 'concept-scheme' ? this.state.selectedVocabularyUri : null
        });
    }

    async openVocabularyTurtleModal() {
        const result = await this.exportService.generateVocabularyTurtle(this.props.vocabulary);
        this.setState({
            turtleText: result.success ? result.content! : '# Failed to generate Turtle',
            turtleModalTitle: 'Vocabulary (Turtle)',
            showTurtleModal: true
        });
    }

    async openDomainModelTurtleModal() {
        const knowledgeGraph = this.knowledgeGraph;
        const diagram = this.diagram;
        if (!knowledgeGraph || !diagram) return;

        const result = await this.exportService.generateKnowledgeGraphTurtle(knowledgeGraph, this.state.domainPrefixes);
        const title = diagram.editingMode === 'ABox' ? 'Knowledge Graph (Turtle)' : 'Model (Turtle)';
        this.setState({
            turtleText: result.success ? result.content! : '# Failed to generate Turtle',
            turtleModalTitle: result.success ? title : 'Export Error',
            showTurtleModal: true
        });
    }

    openDiagramTurtleModal() {
        const diagram = this.diagram;
        const knowledgeGraph = this.knowledgeGraph;
        if (!diagram || !knowledgeGraph) return;

        const result = this.exportService.generateDiagramTurtle(diagram, knowledgeGraph, this.state.domainPrefixes);
        this.setState({
            turtleText: result.success ? result.content! : '# Failed to generate Turtle',
            turtleModalTitle: 'Visible Diagram (Turtle)',
            showTurtleModal: true
        });
    }

    openEssentialTurtleModal = (ttl: string, individualName: string) => {
        this.setState({
            turtleText: ttl,
            turtleModalTitle: `Essential Properties: ${individualName} (Turtle)`,
            showTurtleModal: true
        });
    }

    closeTurtleModal() {
        this.setState({ showTurtleModal: false });
    }

    openBoundedContextInfoModal = () => {
        this.setState({ showBoundedContextInfoModal: true });
    }

    closeBoundedContextInfoModal = () => {
        this.setState({ showBoundedContextInfoModal: false });
    }

    openLoadUrlModal = () => {
        this.setState({ showLoadUrlModal: true });
    }

    closeLoadUrlModal = () => {
        this.setState({ showLoadUrlModal: false });
    }

    handleLoadFromUrl = async (url: string) => {
        const bc = this.boundedContext;
        if (!bc) {
            console.error('Cannot load from URL: no workspace available');
            return;
        }

        // Check if this URL is already registered as a domain
        const existingDomain = getUrlDomainBySourceUrl(url);
        if (existingDomain) {
            console.debug(`App: URL domain already exists, switching to it: ${existingDomain.label}`);
            this.handleDomainChange(existingDomain);
            return;
        }

        // Show loading modal and track start time
        this._loadingStartTime = Date.now();
        this.setState({ showLoadingModal: true, loadingStatus: 'Initializing...' });

        // Progress callback to update loading status
        const onProgress = (status: string) => {
            this.setState({ loadingStatus: status });
        };

        try {
            // Load the ontology to get metadata
            const result = await bc.loadFromUrl(url, onProgress);

            if (!result.success) {
                throw new Error(result.error || 'Failed to load ontology');
            }

            console.debug(`App: Loaded ontology from URL: ${url}, format: ${result.format}`);

            // Find the loaded schema by sourceUrl
            const loadedSchema = bc.getAllOntologies().find(o => o.sourceUrl === url);
            if (!loadedSchema) {
                console.error('App: Could not find loaded ontology schema');
                throw new Error('Failed to find loaded ontology');
            }

            // Get label from ontology metadata
            const label = bc.getOntologyLabel(loadedSchema.getNamespace()) || loadedSchema.name;

            // Create and register URL domain config
            const urlDomainConfig = createUrlDomainConfig(loadedSchema, url, label);
            registerUrlDomain(urlDomainConfig);

            // Persist the URL with metadata for reload on next session (no fetch needed)
            this.persistenceService.addUrlDomain(url, urlDomainConfig.id, urlDomainConfig.label, urlDomainConfig.ns);

            console.debug(`App: Registered URL domain: ${urlDomainConfig.label} (${urlDomainConfig.id})`);

            // Hide loading modal before domain change (which will show its own loading)
            this.setState({ showLoadingModal: false, loadingStatus: '' });

            // Switch to the newly loaded domain (this clears workspace and reloads)
            this.handleDomainChange(urlDomainConfig);
        } catch (error) {
            // Hide loading modal on error
            this.setState({ showLoadingModal: false, loadingStatus: '' });
            throw error;
        }
    }

    downloadTurtle() {
        this.exportService.downloadTurtle(this.state.turtleText || '');
    }

    exportToJson() {
        this.exportService.exportToJson(this.props.workspace.getRootDomains(), this.boundedContext);
    }

    async exportToTurtle() {
        await this.exportService.exportToTurtle(this.props.workspace.getRootDomains(), this.boundedContext);
    }

    /**
     * Show example picker dialog and load selected example.
     * Uses examples from the domain matching the current modeling language.
     */
    showExamplePicker() {
        const domain = this.getModelingLanguageDomain();
        if (!domain) {
            console.warn('No modeling language selected - cannot load examples');
            return;
        }
        const examples = domain.exampleTtl;
        if (!examples) return;

        const filenames = Object.keys(examples);
        if (filenames.length === 0) return;

        if (filenames.length === 1) {
            // Only one example, load it directly
            this.loadExample(filenames[0]);
        } else {
            // Multiple examples, show picker
            const options = filenames.map((f, i) => `${i + 1}. ${f}`).join('\n');
            const choice = prompt(`Select an example to load:\n${options}\n\nEnter number:`);
            if (choice) {
                const index = parseInt(choice, 10) - 1;
                if (index >= 0 && index < filenames.length) {
                    this.loadExample(filenames[index]);
                }
            }
        }
    }

    /**
     * Load an example TTL file into a new diagram.
     * Uses examples from the domain matching the current modeling language.
     */
    async loadExample(filename: string) {
        // Cannot load example if no diagram is active
        if (!this.props.diagramId) {
            console.error('Cannot load example: no active diagram');
            return;
        }

        const bc = this.boundedContext;
        if (!bc) {
            console.error('Cannot load example: no workspace available');
            return;
        }

        const domain = this.getModelingLanguageDomain();
        if (!domain) {
            console.error('Cannot load example: no modeling language selected');
            return;
        }

        const examples = domain.exampleTtl;
        if (!examples || !examples[filename]) {
            console.error(`Example file not found: ${filename}`);
            return;
        }

        const ttlContent = examples[filename];

        try {
            // Parse the example TTL to get axioms
            // Pass bounded context so the parser can resolve external property declarations from loaded ontologies
            const ontology = await parseTurtle(ttlContent, { boundedContext: bc });

            // Extract individual URIs and their class types from axioms
            const individualClasses = new Map<string, string>(); // individualUri -> classUri
            const individualLabels = new Map<string, string>(); // individualUri -> label
            const objectPropertyAssertions: Array<{ source: string; target: string; property: string }> = [];
            const dataPropertyAssertions: Array<{ individual: string; property: string; value: string }> = [];

            for (const axiom of ontology.axioms) {
                if (isClassAssertion(axiom)) {
                    // ClassAssertion gives us the type of each individual
                    const ind = axiom.individual;
                    const classExpr = axiom.classExpression;
                    if (ind.type === 'NamedIndividual' && classExpr.type === 'Class') {
                        const indUri = iriToString(ind.iri);
                        const classUri = iriToString(classExpr.iri);
                        // Skip owl:NamedIndividual type, keep the actual class type
                        if (classUri !== 'http://www.w3.org/2002/07/owl#NamedIndividual') {
                            individualClasses.set(indUri, classUri);
                        }
                    }
                } else if (isAnnotationAssertion(axiom)) {
                    // AnnotationAssertion gives us labels
                    if (iriToString(axiom.property.iri) === 'http://www.w3.org/2000/01/rdf-schema#label') {
                        const subject = axiom.subject;
                        const value = axiom.value;
                        // subject can be IRI or AnonymousIndividual; we only handle IRI cases
                        // value can be IRI, AnonymousIndividual, or Literal; we only handle Literal
                        if ('kind' in subject && (subject.kind === 'FullIRI' || subject.kind === 'AbbreviatedIRI') && 'lexicalForm' in value) {
                            const subjectUri = iriToString(subject);
                            individualLabels.set(subjectUri, value.lexicalForm);
                        }
                    }
                } else if (isObjectPropertyAssertion(axiom)) {
                    // ObjectPropertyAssertion gives us the relationships
                    // Now properly parsed because workspace provides known object property URIs
                    const sourceInd = axiom.sourceIndividual;
                    const targetInd = axiom.targetIndividual;
                    const propExpr = axiom.property;

                    if (sourceInd.type === 'NamedIndividual' &&
                        targetInd.type === 'NamedIndividual' &&
                        propExpr.type === 'ObjectProperty') {
                        objectPropertyAssertions.push({
                            source: iriToString(sourceInd.iri),
                            target: iriToString(targetInd.iri),
                            property: iriToString(propExpr.iri)
                        });
                    }
                } else if (isDataPropertyAssertion(axiom)) {
                    // DataPropertyAssertion gives us data property values (hasName, hasDateOfBirth, etc.)
                    const sourceInd = axiom.sourceIndividual;
                    const propExpr = axiom.property;
                    const targetValue = axiom.targetValue;

                    if (sourceInd.type === 'NamedIndividual' && propExpr.type === 'DataProperty') {
                        dataPropertyAssertions.push({
                            individual: iriToString(sourceInd.iri),
                            property: iriToString(propExpr.iri),
                            value: targetValue.lexicalForm
                        });
                    }
                }
            }

            // Create new KnowledgeGraph
            const kgUri = 'http://example.org/party/data/';
            const newKnowledgeGraph = new KnowledgeGraph("FamilyExample", kgUri);

            // Set class hierarchy for type checking
            if (this.state.classHierarchy) {
                newKnowledgeGraph.setClassHierarchy(this.state.classHierarchy);
            }

            // Create Individual objects for each parsed individual
            const individualMap = new Map<string, Individual>(); // uri -> Individual

            // Layout positions for family tree (3 generations)
            const positions = this.calculateFamilyTreePositions(individualClasses, objectPropertyAssertions);

            for (const [indUri, classUri] of individualClasses) {
                // Find the SchemaClass from taxonomy
                const schemaClass = this.findSchemaClassByUri(classUri);
                if (!schemaClass) {
                    console.warn(`SchemaClass not found for ${classUri}`);
                    continue;
                }

                // Get label from parsed data
                const label = individualLabels.get(indUri) || extractLocalName(indUri);

                // Create concept for the individual
                const concept = new Concept(
                    indUri,
                    label,
                    [],
                    [],
                    ""
                );

                // Create Individual
                const individual = new Individual(concept, indUri, label, schemaClass);
                individualMap.set(indUri, individual);
                newKnowledgeGraph.addIndividual(individual);
            }

            // Apply data property values to individuals
            for (const assertion of dataPropertyAssertions) {
                const individual = individualMap.get(assertion.individual);
                if (individual) {
                    individual.setPropertyValue(assertion.property, assertion.value);
                }
            }

            // Create Relationship objects for object property assertions
            for (const assertion of objectPropertyAssertions) {
                const sourceInd = individualMap.get(assertion.source);
                // Try local ABox map first, then workspace (searches both ABox and TBox)
                const targetNode: Node | undefined = individualMap.get(assertion.target)
                    ?? bc.getIndividualByUri(assertion.target);

                if (sourceInd && targetNode) {
                    // Find the ObjectProperty from relationship taxonomy
                    const objectProperty = this.findObjectPropertyByUri(assertion.property);
                    if (objectProperty) {
                        const relationship = new Relationship(sourceInd, objectProperty, targetNode);
                        newKnowledgeGraph.addRelationship(relationship);
                    }
                }
            }

            // Reuse the existing diagram - clear its elements but preserve settings
            const diagram = this.diagram;
            if (!diagram) {
                console.error('Cannot load example: no active diagram');
                return;
            }

            // Clear existing elements from the diagram
            diagram.classes = [];
            diagram.relationships = [];
            diagram.inferredRelationships = [];

            const diagramClassMap = new Map<string, DiagramClass>();

            // Create DiagramClasses with calculated positions
            for (const [indUri, individual] of individualMap) {
                const pos = positions.get(indUri) || { x: 100, y: 100 };
                const diagramClass = new DiagramClass(individual, pos.x, pos.y, 150, 60);
                diagram.addClass(diagramClass);
                diagramClassMap.set(indUri, diagramClass);
            }

            // Create DiagramRelationships
            for (const [, relationship] of newKnowledgeGraph.relationships) {
                const sourceDC = diagramClassMap.get(relationship.subject.uri);
                const targetDC = diagramClassMap.get(relationship.object.uri);
                if (sourceDC && targetDC) {
                    const diagramRel = new DiagramRelationship(relationship, sourceDC, targetDC);
                    diagram.addRelationship(diagramRel);
                }
            }

            // Set ontology schema on the new knowledge graph for inference rules
            // Use the modeling language domain (already defined above)
            let primaryOntology = bc.getOntologyByIRI(domain.ns);
            if (!primaryOntology) {
                // Find by namespace match
                primaryOntology = bc.getAllOntologies().find(
                    schema => schema.getNamespace() === domain.ns
                );
            }
            if (primaryOntology) {
                newKnowledgeGraph.setOntologySchema(primaryOntology);
                console.debug('loadExample: Set ontology schema on knowledge graph:', primaryOntology.ontologyIRI);
            }

            // Update the diagram's dataset to the new knowledge graph
            diagram.datasetId = newKnowledgeGraph.id ?? diagram.id;
            bc.setDataset(
                { id: diagram.datasetId, name: newKnowledgeGraph.name || 'UserKnowledgeGraph', type: 'KnowledgeGraph' },
                newKnowledgeGraph
            );

            this.setState({
                selectedElement: null,
                proposedConcepts: [],
                draggingConceptUri: null,
                toolboxSelectedClass: null,
                viewMode: 'diagram'  // Ensure we show the diagram
            }, () => {
                // After state update, notify diagram changed to trigger re-render
                this.notifyDiagramChanged(() => {
                    // After state update, unselect all
                    this.diagramEditor.current?.unselectAll();
                    // Enable and start force simulation for the loaded example
                    this.diagramEditor.current?.setForceLayout(true, true, 0.8);
                    // Run inference if enabled
                    if (this.diagram?.inferenceEnabled) {
                        console.debug('loadExample: Running inference after loading example');
                        this.runInference();
                    }
                });
            });

        } catch (error) {
            console.error('Error loading example:', error);
            alert('Failed to load example: ' + (error instanceof Error ? error.message : String(error)));
        }
    }

    /**
     * Calculate family tree positions based on generations (using hasParent relationships)
     */
    calculateFamilyTreePositions(
        individualClasses: Map<string, string>,
        relationships: Array<{ source: string; target: string; property: string }>
    ): Map<string, { x: number; y: number }> {
        const positions = new Map<string, { x: number; y: number }>();

        // Build parent-child graph
        const children = new Map<string, Set<string>>(); // parent -> children
        const parents = new Map<string, Set<string>>(); // child -> parents

        for (const rel of relationships) {
            // hasParent means source is child, target is parent
            if (rel.property.includes('hasParent')) {
                if (!parents.has(rel.source)) parents.set(rel.source, new Set());
                parents.get(rel.source)!.add(rel.target);

                if (!children.has(rel.target)) children.set(rel.target, new Set());
                children.get(rel.target)!.add(rel.source);
            }
        }

        // Determine generations (0 = no parents, 1 = has parents who have no parents, etc.)
        const generations = new Map<string, number>();
        const allIndividuals = Array.from(individualClasses.keys());

        const getGeneration = (uri: string, visited: Set<string>): number => {
            if (generations.has(uri)) return generations.get(uri)!;
            if (visited.has(uri)) return 0; // Cycle detection
            visited.add(uri);

            const parentSet = parents.get(uri);
            if (!parentSet || parentSet.size === 0) {
                generations.set(uri, 0);
                return 0;
            }

            let maxParentGen = -1;
            for (const parent of parentSet) {
                maxParentGen = Math.max(maxParentGen, getGeneration(parent, visited));
            }
            const gen = maxParentGen + 1;
            generations.set(uri, gen);
            return gen;
        };

        for (const ind of allIndividuals) {
            getGeneration(ind, new Set());
        }

        // Group by generation
        const genGroups = new Map<number, string[]>();
        for (const [uri, gen] of generations) {
            if (!genGroups.has(gen)) genGroups.set(gen, []);
            genGroups.get(gen)!.push(uri);
        }

        // Assign positions
        const baseY = 100;
        const ySpacing = 200;
        const xSpacing = 200;

        for (const [gen, members] of genGroups) {
            const y = baseY + gen * ySpacing;
            const totalWidth = (members.length - 1) * xSpacing;
            const startX = 400 - totalWidth / 2;

            members.forEach((uri, i) => {
                positions.set(uri, { x: startX + i * xSpacing, y });
            });
        }

        return positions;
    }

    /**
     * Find SchemaClass by URI from the class taxonomy
     */
    findSchemaClassByUri(classUri: string): SchemaClass | undefined {
        const taxonomy = this.state.classTaxonomy;
        if (!taxonomy) return undefined;

        const findInNodes = (nodes: ClassTaxonomyNode[]): ClassTaxonomyNode | undefined => {
            for (const node of nodes) {
                if (node.uri === classUri) return node;
                if (node.children) {
                    const found = findInNodes(node.children);
                    if (found) return found;
                }
            }
            return undefined;
        };

        const node = findInNodes(taxonomy);
        if (!node) return undefined;

        // Create SchemaClass from taxonomy node
        const classConcept = new Concept(node.uri, node.label, [], [], node.description || "");
        const dataProperties = this.state.dataPropertiesMap?.get(node.uri);
        const subClassOf = this.state.objectPropertyRestrictionsMap?.get(node.uri);

        return new SchemaClass('class', classConcept, {
            label: node.label,
            stereotype: node.stereotype,
            categoryColor: node.categoryColor,
            dataProperties: dataProperties,
            subClassOf: subClassOf
        });
    }

    /**
     * Get the domain config for the currently selected modeling language.
     * Falls back to activeDomain if no modeling language is selected.
     */
    getModelingLanguageDomain(): DomainConfig | undefined {
        const modelingLanguageIri = this.diagram?.modelingLanguageIri;
        if (!modelingLanguageIri) {
            return undefined;
        }
        // Find domain by namespace - modeling language IRI typically matches domain namespace
        return getDomainByNamespace(modelingLanguageIri);
    }

    /**
     * Find ObjectProperty by URI from the relationship taxonomy
     */
    findObjectPropertyByUri(propertyUri: string): ObjectProperty | undefined {
        const taxonomy = this.state.relationshipTaxonomy;
        if (!taxonomy) return undefined;

        const findInNodes = (nodes: RelationshipTaxonomyNode[]): RelationshipTaxonomyNode | undefined => {
            for (const node of nodes) {
                if (node.uri === propertyUri) return node;
                if (node.children) {
                    const found = findInNodes(node.children);
                    if (found) return found;
                }
            }
            return undefined;
        };

        const node = findInNodes(taxonomy);
        if (!node || !node.uri) return undefined;

        return {
            uri: node.uri,
            label: node.label,
            relationshipType: node.type,
            sourceMarker: node.sourceMarker,
            targetMarker: node.targetMarker,
            strokeStyle: node.strokeStyle
        };
    }

    createNewDiagram() {
        if (!confirm("Create a new diagram? Your current diagram will be preserved.")) {
            return;
        }

        // Navigate to a new diagram with a fresh UUID (using React Router to avoid page refresh)
        const newDiagramId = crypto.randomUUID();
        if (this.props.navigateToDiagram) {
            this.props.navigateToDiagram(newDiagramId);
        } else {
            // Fallback for cases where navigateToDiagram is not provided
            window.location.href = `/diagrams/${newDiagramId}`;
        }
    }

    selectConcept(concept: Concept) {
        console.log("Selected concept: " + concept.uri);
        console.log({concept});

        return;

        // display the concept in #properties
        //$("#properties").empty();
        // $("#selectedElement").html(concept.prefLabel);
        // $("#selectedElementType").text("Concept");
        // $("#selectedElementProperties")
        //     .empty()
        //     .append(`<p>${concept.prefLabel}</p>`);
        // $("#proposedProperties").empty();
        // $("#selectedElementDefinition").text(concept.definition);
        // $("#proposedDefinitions").empty();
        // $("#selectedElementRelationships").empty();
        // concept.broader.forEach((broader: string) => {
        //     $("#selectedElementRelationships").append(`<p>broader ${broader}</p>`);
        // });
        // concept.narrower.forEach((narrower: string) => {
        //     $("#selectedElementRelationships").append(`<p>narrower ${narrower}</p>`);
        // });
    }

}

export {App};
